// Enums
const VideoState = {
  PLAYING: "PLAYING",
  PAUSED: "PAUSED",
};
const VideoControlIconsSet = {
  PLAYING: "PLAYING",
  PAUSED: "PAUSED",
};
const FrameDepth = {
  INCH75: "75inch",
  INCH150: "150inch",
};
const CanvasSize = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large",
  X_LARGE: "x-large",
};
const AspectRatio = {
  SQUARE: "square",
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
};
const CanvasPrice = {
  SMALL: 69,
  MEDIUM: 89,
  LARGE: 99,
  X_LARGE: 159,
};

// Global Variables
let cv=null;
let didImageTargetEvaluationPass=false;
let isImageUploaded = false;
let isVideoUploaded = false;
let wasDonePositioningButtonPressed = false;
let wasCanvasExperienceNameAdded = false;
let videoUploadFile = null;
let imageUploadFile = null;
let selectedAspectRatio = AspectRatio.LANDSCAPE;
let selectedFrameDepth = FrameDepth.INCH150;
let selectedCanvasSize = CanvasSize.LARGE;
let exactFitSideImage = "width";
let exactFitSideVideo = "width";
let currentVideoControlIcons = "";
let iconBounds = {
  play: null,
  pause: null,
  reset: null,
};
let imageCroppingData = {
  imageUploadFile: null,
  originalWidth: 0,
  originalHeight: 0,
  originalLeft: 0,
  originalTop: 0,
  cropWidth: 0,
  cropHeight: 0,
};
let videoCroppingData = {
  videoUploadFile: null,
  originalWidth: 0,
  originalHeight: 0,
  originalLeft: 0,
  originalTop: 0,
  cropWidth: 0,
  cropHeight: 0,
};
let orderSnapshot = {
  frameDepth: null,
  canvasSize: null,
  aspectRatio: null,
  qrCodeImageBlob: null,
  imageUploadFile: null,
  videoUploadFile: null,
  imageCroppingData: null,
  videoCroppingData: null,
  canvasExperienceName: null,
  canvasPrice: null,
  canvasQuantity: 1,
  imageTargetEvaluationPass: null,
  productCode: null,
  arPackageCode: null,
  orderId: null,
  jobId: null,
  mockupThumbnail: null,
  mockupThumbnailUrl: null,
}

// Global Firestore field names
const FIELD_JOB_ID = "jobId";
const FIELD_ORDER_ID = "orderId";
const FIELD_ORDER_PAYMENT_STATUS = "orderPaymentStatus";
const FIELD_SHOPIFY_SYSTEM_ID = "shopifySystemId";
const FIELD_SHOPIFY_LINE_ITEM_ID = "shopifyLineItemId";
const FIELD_SHOPIFY_FULFILLMENT_ORDER_ID = "shopifyFulfillmentOrderId";
const FIELD_SHOPIFY_FULFILLMENT_LINE_ITEM_ID = "shopifyFulfillmentLineItemId";
const FIELD_PRODUCT_CODE = "productCode";
const FIELD_AR_PACKAGE_CODE = "arPackageCode";
const FIELD_CANVAS_EXPERIENCE_NAME = "canvasExperienceName";
const FIELD_CUSTOMER_FIRST_NAME = "customer_firstName";
const FIELD_CUSTOMER_LAST_NAME = "customer_lastName";
const FIELD_CUSTOMER_ADDRESS_LINE1 = "customer_addressLine1";
const FIELD_CUSTOMER_ADDRESS_LINE2 = "customer_addressLine2";
const FIELD_CUSTOMER_CITY = "customer_city";
const FIELD_CUSTOMER_STATE = "customer_state";
const FIELD_CUSTOMER_ZIP = "customer_zip";
const FIELD_CUSTOMER_COUNTRY = "customer_country";
const FIELD_CUSTOMER_EMAIL = "customer_email";
const FIELD_CUSTOMER_PHONE = "customer_phone";
const FIELD_CUSTOMER_SHIPPING_METHOD = "customer_shippingMethod";
const FIELD_CUSTOMER_CUSTOMER_TIME_ORDER_DATE =
  "customer_customerTimeOrderDate";
const FIELD_QRCODE_SOURCE_UPLOAD_STATUS = "qrCode_sourceUploadStatus";
const FIELD_QRCODE_SOURCE_PATH = "qrCode_sourcePath";
const FIELD_IMAGE_SOURCE_UPLOAD_STATUS = "image_sourceUploadStatus";
const FIELD_IMAGE_SOURCE_PATH = "image_sourcePath";
const FIELD_IMAGE_TARGET_EVALUATION_PASS = "image_targetEvaluationPass";
const FIELD_VIDEO_SOURCE_UPLOAD_STATUS = "video_sourceUploadStatus";
const FIELD_VIDEO_SOURCE_PATH = "video_sourcePath";
const FIELD_JOB_CREATION_DATE = "job_creationDate";
const FIELD_JOB_JOB_STATUS = "job_jobStatus";
const FIELD_JOB_STATUS_MAP_IMAGE_STATUS = "imageStatus";
const FIELD_JOB_STATUS_MAP_VIDEO_STATUS = "videoStatus";
const FIELD_JOB_STATUS_MAP_QRCODE_STATUS = "qrCodeStatus";
const FIELD_JOB_STATUS_MAP_AR_PACKAGE_STATUS = "arPackageStatus";
const FIELD_JOB_STATUS_MAP_CUSTOMCAT_STATUS = "customCatStatus";
const FIELD_CANVAS_DEPTH = "canvas_depth";
const FIELD_CANVAS_SHAPE = "canvas_shape";
const FIELD_CANVAS_SIZE = "canvas_size";
const FIELD_CANVAS_PRICE = "canvas_price";
const FIELD_CANVAS_QUANTITY = "canvas_quantity";
const FIELD_MEDIA_PROCESSING_STATUS = "mediaProcessingStatus";
const FIELD_MOCKUP_THUMBNAIL_FOR_CART_PATH = "mockupThumbnailForCartPath";

// Firestore Collection name
const FIRESTORE_JOB_TRACKING_COLLECTION = "CustomCanvasJobs";

// Media Processing statuses 
const MediaProcessingStatus  = {
    WAITING_FOR_PAYMENT: "waitingForPayment",
    PAYMENT_CONFIRMED: "paymentConfirmed",
    MEDIA_PROCESSING_INITIATED: "mediaProcessingInitiated",
    PAYMENT_FAILED_MEDIA_PROCESSING_NOT_AUTHORIZED:  "paymentFailed_mediaProcessingNotAuthorized",
};


// Detect Safari browser
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const isChrome =
  /chrome|chromium|crios/i.test(navigator.userAgent) &&
  !/edge|edg/i.test(navigator.userAgent);

// Import the necessary Firebase Storage functions required
import {
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "https://www.gstatic.com/firebasejs/10.8.0/firebase-storage.js";

// Import the necessary Firestore functions required
import {
  getFirestore,
  doc,
  collection,
  addDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from "https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js";

// Import the necessary Firebase Authorization functions required
import {
  getAuth,
  signInAnonymously,
} from "https://www.gstatic.com/firebasejs/10.8.0/firebase-auth.js";

// Sign in to Firebase app using anonymous sign-in
const auth = getAuth();
signInAnonymously(auth)
  .then(() => {
    console.log("Signed in as an anonymous user.");
  })
  .catch((error) => {
    console.error(`Anonymous sign-in failed: ${error.message}`);
  });

// Function to load the custom opencv.js file
function loadOpenCV() {
  var opencvLoader = document.getElementById('opencv-loader');
  var opencvUrl = opencvLoader.getAttribute('data-opencv-url');
  
  var script = document.createElement('script');
  script.src = opencvUrl;
  script.onload = function() {
    // For some reason Module.[function name] is the only one that works. So we create
    // a global cv object and assign Module to it once it's loaded
    cv = Module;
    
    // OpenCV.js is successfully loaded, you can call your functions here
    console.log('OpenCV.js is successfully loaded.');

    // Uncomment line below to test that functions are loading properly
    //console.log('Available OpenCV functions as cv:', Object.keys(cv));
  };
  script.onerror = function() {
    console.log('Failed to load OpenCV.js.');
  };
  document.head.appendChild(script);
}

// Initialize event listeners for all of the buttons once DOMContent has loaded
document.addEventListener("DOMContentLoaded", function () {

  // Load custom OpenCV file so we test images to see if they make good target images
  loadOpenCV();
  
  // Event listeners for upload buttons
  if (document.getElementById("upload-image-btn")) {
    document
      .getElementById("upload-image-btn")
      .addEventListener("click", uploadImage);
  }
  if (document.getElementById("upload-video-btn")) {
    document
      .getElementById("upload-video-btn")
      .addEventListener("click", uploadVideo);
  }

  // Event listeners for aspect ratio buttons
  if (document.getElementById("aspect-square")) {
    document
      .getElementById("aspect-square")
      .addEventListener("click", function () {
        changeAspectRatio(AspectRatio.SQUARE);
      });
  }
  if (document.getElementById("aspect-landscape")) {
    document
      .getElementById("aspect-landscape")
      .addEventListener("click", function () {
        changeAspectRatio(AspectRatio.LANDSCAPE);
      });
  }
  if (document.getElementById("aspect-portrait")) {
    document
      .getElementById("aspect-portrait")
      .addEventListener("click", function () {
        changeAspectRatio(AspectRatio.PORTRAIT);
      });
  }

  // Event listeners for the I'm Done Positioning button
  if (document.getElementById("done-positioning-button")) {
    document
      .getElementById("done-positioning-button")
      .addEventListener("click", function () {
        // Get the visible portion of the position image to evaluate as an image target
        getVisiblePortionOfImage()
          .then((image) => {

            // Evaluate image to see if it is a good image target
            const isGoodImageTarget = evaluateImageForARTarget(image);

            // If it is a good target, proceed with processing
            if (isGoodImageTarget) {
              wasDonePositioningButtonPressed = true;
              checkBothDoneButtonsPressed();
              handleVideoDone();
              handleImageDone();              
            } else {
              // If it is a bad target, show popup message and let user continue positioning when they close popup
              showPoorImageTargetPopupMessage(false); // Pass flag indicating this is not an initial upload
            }
          })
          .catch((error) => {
            console.error(error.message);
          });
      });
  }

  // Update the canvas size dropdown options on load 
  updateCanvasSizeDropdownOptionsDisplay();

  // Function to update the price on the webpage
  function updateCanvasPriceOnWebPage() {
    const currentCanvasPrice = getCanvasPrice();
    if (document.getElementById("price-value")) {
      const priceValue = document.getElementById("price-value");
      priceValue.textContent = currentCanvasPrice.toFixed(2);
    }
  }

  // Set up event listener for canvas size changes
  if (document.getElementById("canvas-size-select")) {
    document
      .getElementById("canvas-size-select")
      .addEventListener("change", function () {
        selectedCanvasSize = this.value;
        updateCanvasPriceOnWebPage();
        resizeQrCodeSampleImage();
      });

    // Set initial canvas price on page load and set QR code sample image size and position
    updateCanvasPriceOnWebPage();
    resizeQrCodeSampleImage();
  }

  // Set up event listeners for aspect ratio changes to also
  // update the canvas size display options
  document
    .querySelectorAll("#aspect-ratio-section button")
    .forEach((button) => {
      button.addEventListener("click", function () {
        selectedAspectRatio = this.getAttribute("data-aspect-ratio");
        updateCanvasSizeDropdownOptionsDisplay();

        // Add an event listener that listens for the end of the canvas shape changing transition and then
        // calls the QR code resizing function, and then removes the event listener
        const previewWindow = document.querySelector("#image-preview");
        const handleTransitionEnd = () => {
          resizeQrCodeSampleImage();
          previewWindow.removeEventListener('transitionend', handleTransitionEnd);
        };

        // Add the transitionend event listener to the preview window
        previewWindow.addEventListener('transitionend', handleTransitionEnd);
      });
    });  

  // Set up event listener for the Order Confirmation button
  if (document.getElementById("order-confirmation-button")) {
    document
      .getElementById("order-confirmation-button")
      .addEventListener("click", function () {
        showCustomCanvasConfirmationPopup();
      });
  }

  // Set up an event listener for the Make Changes button (that closes the Custom Canvas Confirmation Popup)
  if (document.getElementById("make-changes-button")) {
    document
      .getElementById("make-changes-button")
      .addEventListener("click", function () {

        // Hide confirmation popup window and re-enable scrolling on page
        document.getElementById('custom-canvas-confirmation-popup').style.opacity = 0;
        setTimeout(function() {
          document.getElementById('custom-canvas-confirmation-popup').classList.add('hidden');
          document.body.classList.remove('no-scroll');
        }, 300); // Matches the CSS fade-out time        
      });
  }

  // Set up an event listener for the force cancellation Okay button (this closes out the progress overlay and
  // the Custom Canvas Confirmation popup after 3 advising the user of 3 failed retry attempts for the uploads)
  document.getElementById('forcedCancellationOkayButton').addEventListener('click', () => {

    // Rehide the cancel message and the okay button
    document.getElementById('cancelMessage').style.display = 'none';
    document.getElementById('addToCartErrorMessage').style.display = 'none';
    document.getElementById('forcedCancellationButtonContainer').style.display = 'none';  

    // Switch the overlay messages back to the normal overlay message and progress text
    const overlayMessage = document.getElementById('overlayMessage');
    const progressText = document.getElementById('progressText');
    overlayMessage.style.display = 'block';
    progressText.style.display = 'block'; // Ensure the progress text is visible

    // Reset progress bar to 0% and to Preparing Uploads text
    updateProgressBar(0, "Preparing Uploads...");    

    // Hide the overlay and confirmation popup screens
    const progressOverlay = document.getElementById('progressOverlay');
    const confirmationPopup = document.getElementById('custom-canvas-confirmation-popup');
    progressOverlay.style.opacity = 0; // Fade out the overlay

    // Hide confirmation popup and re-enable scrolling on the page after a slight delay
    confirmationPopup.style.opacity = 0;
    setTimeout(function () {
      confirmationPopup.classList.add('hidden');
      document.body.classList.remove('no-scroll');
      progressOverlay.classList.add('hidden'); // Hide the progress overlay
    }, 300); // Matches the CSS fade-out time

  });

  // Set up an event listener for the Add to Cart button
  if (document.getElementById("add-to-cart-button")) {
    document
      .getElementById("add-to-cart-button")
      .addEventListener("click", function () {
        handleAddToCart();
      })
  }

  // Set up an event listener for the Canvas Name Experience section
  if (document.getElementById("canvas-name-input")) {
    document
      .getElementById("canvas-name-input")
      .addEventListener("input", function () {
        const canvasName = document
          .getElementById("canvas-name-input")
          .value.trim();
        if (canvasName !== "") {
          document.getElementById("order-confirmation-section").style.display =
            "block";
          wasCanvasExperienceNameAdded = true;
        } else {
          document.getElementById("order-confirmation-section").style.display =
            "none";
          wasCanvasExperienceNameAdded = false;
        }
      });
  }

  // Set up an event listener for the close button on the Poor Image Target Popup Window
  if (document.getElementById("close-poor-image-target-popup")) {
    document
      .getElementById("close-poor-image-target-popup")
      .addEventListener("click", hidePoorImageTargetPopupMessage);
  }
  
});

// Setting up an event listener to set the selected canvas size and frame depth to whatever the page
// has as the current value -- This is helpful when the page caches values and restores them if the
// naviagtes away from the page then back to it
window.addEventListener('pageshow', (event) => {

  // Always trigger the QR code resize when the page is shown
  if (document.getElementById("canvas-size-select")) {
    const canvasSizeDropDown = document.getElementById("canvas-size-select");
    selectedCanvasSize = canvasSizeDropDown.value;
    
    // .75 inch frame discontinued at CustomCat so we're changing this to force the 1.5 inch frame
    selectedFrameDepth = FrameDepth.INCH150;
    
    resizeQrCodeSampleImage();
  }
});




// Function to use OpenCV.js library to determine if an image is suitable to be an image
// target for AR tracking -- Returns true if it is a good target and false if it's a poor target
function evaluateImageForARTarget(imageElement) {

  // Only continue if OpenCV has loaded -- This checks for both null and undefined
  if (cv == null) {
    console.log("Returning from evaluateImageForARTarget(): OpenCV.js is not loaded.");
    didImageTargetEvaluationPass = false;
    return true;
  }
  
  // Read the image and convert it to grayscale
  let src = cv.imread(imageElement);
  let gray = new cv.Mat();
  cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY, 0);

  // Feature detection using ORB
  let numKeyPointsToDetect = 1000; 
  let orb = new cv.ORB(numKeyPointsToDetect);
  let keypoints = new cv.KeyPointVector();
  let descriptors = new cv.Mat();
  orb.detectAndCompute(gray, new cv.Mat(), keypoints, descriptors);

  // Edge detection using Canny
  let edges = new cv.Mat();
  cv.Canny(gray, edges, 50, 150, 3, false);

  // Evaluate the number of key points and edges
  let numKeyPoints = keypoints.size();
  let numEdges = cv.countNonZero(edges);

  // Cleaning up memory
  gray.delete();
  orb.delete();
  keypoints.delete();
  descriptors.delete();
  edges.delete();

  // Determine suitability based on the number of key points and edges
  let suitabilityMessage;
  let isGoodImageTarget = true;
  didImageTargetEvaluationPass = true;
  if (numKeyPoints > 500 && numEdges > 1000) {
    suitabilityMessage = 'Good candidate for AR target.';
  } else if (numKeyPoints > 200 && numEdges > 500) {
    suitabilityMessage = 'Moderate candidate for AR target. Consider improving contrast and texture.';
  } else {
    suitabilityMessage = 'Poor candidate for AR target. The image lacks sufficient detail and features.';
    isGoodImageTarget = false;
    didImageTargetEvaluationPass = false;
  }

  // Alert the results
  const resultsMessage = `Number of Key Points: ${numKeyPoints}\nNumber of Edges: ${numEdges}\nSuitability: ${suitabilityMessage}`;
  //alert(`Number of Key Points: ${numKeyPoints}\nNumber of Edges: ${numEdges}\nSuitability: ${suitabilityMessage}`);

  return isGoodImageTarget;
}

// Note: On upscaling images -- Input images sent to Replicate to be upscaled should be
// 1440 max in width and height, otherwise the prediction will have a memory fail. So,
// current strategy is to resize any image bigger to down to having the larger side be
// 1440 pixels -- If there are fidelity issues later with people who want to submit
// very high-res images that get downscaled, we can work on splitting image into
// pieces and upscaling, or just resizing very large images -- But for now, 1440 pixels
// can get upscaled 10x to 14,400 pixels with no memory issues. That means larger input
// images will get downsized and then reupscaled to the target size

// Function to upload image
function uploadImage() {

  // Create input element and the types of files the user will be able to accept
  const input = document.createElement("input");
  input.type = "file";
  input.accept = "image/png, image/jpeg, image/webp, image/avif";

  input.onchange = (e) => {    
    const file = e.target.files[0];
    const fileType = file.type;

    // Validate file type
    if (
      fileType !== "image/png" &&
      fileType !== "image/jpeg" &&
      fileType !== "image/webp" &&
      fileType !== "image/avif"
    ) {
      alert("File must be a PNG, JPEG, WEBP, or AVIF image");
      return;
    }

    // Check the file size (limit to 30 MB for images)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 30) {
      alert("File size must be 30 MB or less");
      return;
    }

    // Create a new image element and attempt to load
    const img = new Image();
    img.src = URL.createObjectURL(file);

    // On error event handler in case image is not supported by the browser
    img.onerror = () => {
      alert("This image file is not supported by this browser");
      URL.revokeObjectURL(img.src); // Clean up
    };

    // On load event handler means image was successfully loaded and we can continue
    img.onload = () => {
      // First check to make sure the smaller side of the image is at least 360 pixels
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;
      const smallerSide = Math.min(imgWidth, imgHeight);
      if (smallerSide < 360) {
        alert("Image resolution is too small. The smallest side must be at least 360 pixels.");
        URL.revokeObjectURL(img.src);
        return;
      }
      
      // Assign file to global image file variable and display image
      imageUploadFile = file;
      displayImage(img, file);

      // Reset wasDonePositioningButtonPressed and hide Order Confirmation button and Canvas Experience Name section again
      wasDonePositioningButtonPressed = false;
      document.getElementById("order-confirmation-section").style.display =
        "none";
      document.getElementById("canvas-name-section").style.display = "none";

      // Evaluate image as a viable image target
      const isGoodImageTarget = evaluateImageForARTarget(img);

      if (!isGoodImageTarget) {
        showPoorImageTargetPopupMessage(true); // Pass flag indicating it's the initial upload
      }
    };
  };

  // Slight delay helps with Safari not accepting the input file the first time
  setTimeout(() => {
    input.click();
  }, 500);
}

// Function to upload video
function uploadVideo() {
  // Create input element and the types of files the user will be able to accept
  const input = document.createElement("input");
  input.type = "file";

  // We are no longer limiting file types based on browser as Chrome has been shown to play MOV files that are H.264 and
  // Apple has been shown to play WEBP and WEBM on Safari 16.4+ with full support and with partial support on many versions
  // prior to that
  //
  // For now, we will accept png, jpeg, webp, avif images and mp4, mov, webm videos on all browsers and OS
  input.accept = "video/mp4, video/webm, video/quicktime";
  input.onchange = (e) => {
    const file = e.target.files[0];
    const fileType = file.type;

    // Validate file type
    if (fileType !== "video/mp4" && fileType !== "video/webm" && fileType !== "video/quicktime") {
      // Case: Video is not mp4, webm, or mov (quicktime)
      alert("Only MP4, MOV, and MOV videos are supported.");
      return;
    }  

    // Check the file size (limit to 200 MB for videos)
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 200) {
      alert("File size must be 200 MB or less");
      return;
    }

    // Create a new video element and attempt to load metadata
    // Video load call required for Safari to get first frame to draw to canvas
    // Turn off video controls, we will build our own buttons
    const video = document.createElement("video");  
    video.src = URL.createObjectURL(file);
    //video.preload = "metadata";  
    video.load();
    video.controls = false;
    video.setAttribute("playsinline", ""); // This enables inline playback on iOS devices
    video.setAttribute("webkit-playsinline", ""); // This is for older iOS webviews

    // Turn off default listeners so video does not play/pause when we drag the element around
    video.addEventListener("click", (e) => e.preventDefault());

    // On error event handler in case video is not supported by the browser
    video.onerror = () => {
      URL.revokeObjectURL(video.src); // Clean up
      if (isChrome) {
        alert("This video format is not supported by this browser");
      } else {
        alert(
          "This video format is not supported by this browser, but it may be supported using Chrome"
        );
      }
    };

    // On loaded metadata event handler means video was successfully loaded and we can continue
    video.onloadedmetadata = () => {
      // Check video to see if it is longer than 90 seconds
      const videoDuration = video.duration;
      if (videoDuration > 90) {
        alert("Video length must be under 90 seconds");
        return;
      }
      
      // Assign file to global video file variable and display video
      videoUploadFile = file;
      displayVideo(video, file);

      // Reset wasDonePositioningButtonPressed and hide Order Confirmation button and Canvas Experience Name section again
      wasDonePositioningButtonPressed = false;
      document.getElementById("order-confirmation-section").style.display =
        "none";
      document.getElementById("canvas-name-section").style.display = "none";
    };
  };

  // Slight delay helps with Safari not accepting the input file the first time
  setTimeout(() => {
    input.click();
  }, 100);
}

// Function to display the image once it has loaded
function displayImage(img, file) {
  // Put the filename next to the button
  const fileName = file.name;
  document.getElementById("image-file-name").textContent = fileName;

  // Insert the image into the image container
  const imageContainer = document.querySelector(".image-container");
  imageContainer.innerHTML = ""; // Clear previous image if any
  imageContainer.appendChild(img);

  // Get the dimensions of the preview window dynamically
  const previewWindow = document.querySelector("#image-preview");
  const previewWindowWidth = parseInt(
    window.getComputedStyle(previewWindow).width,
    10
  );
  const previewWindowHeight = parseInt(
    window.getComputedStyle(previewWindow).height,
    10
  );

  // Scale the image to fit the preview window, reposition, and check if both are uploaded
  scaleElement(img, previewWindowWidth, previewWindowHeight);
  resetElementPosition(img);
  isImageUploaded = true;
  checkBothUploaded();

  // Make the image draggable
  const imageElement = img;
  makeDraggable(imageElement);  

  // Scroll down to position Design Options box at the top and have as much of the image frame in view as possible
  const designOptionsBox = document.getElementById("design-options-container");
  if (designOptionsBox) {
    const navBarOffset = 120;
    const scrollToPosition = designOptionsBox.getBoundingClientRect().top + window.pageYOffset - navBarOffset;
    window.scrollTo({
      top: scrollToPosition,
      behavior: "smooth"
    });
  }   

  // Display drag image hint (added an animation trigger reflow so it bounces with each new image added)  
  const hintElement = document.querySelector("#image-preview-wrapper .hint");
  hintElement.classList.remove("visible");
  void hintElement.offsetWidth;
  hintElement.classList.add("visible");
}

// Function to display the video once it has loaded
function displayVideo(video, file) {
  
  // Put the filename next to the button
  const fileName = file.name;
  document.getElementById("video-file-name").textContent = fileName;

  // Create a new canvas element to draw video frames to for when video is not playing
  const canvas = document.createElement("canvas");
  canvas.id = "videoFrameCanvas";
  const ctx = canvas.getContext("2d");
  canvas.style.display = "none"; // Initially hide the canvas

  // Get a reference to the draggable container and insert the video and the canvas into it
  const draggableContainer = document.querySelector("#draggable-container");
  draggableContainer.innerHTML = "";
  draggableContainer.appendChild(video);
  draggableContainer.appendChild(canvas);

  // Get the dimensions of the preview window dynamically
  const previewWindow = document.querySelector("#video-preview");
  const previewWindowWidth = parseInt(
    window.getComputedStyle(previewWindow).width,
    10
  );
  const previewWindowHeight = parseInt(
    window.getComputedStyle(previewWindow).height,
    10
  );

  // Scale the image to fit the preview window, reposition, and check if both are uploaded
  scaleElement(video, previewWindowWidth, previewWindowHeight);
  resetElementPosition(video);
  isVideoUploaded = true;
  checkBothUploaded();

  // Make the draggableContainer draggable
  makeDraggable(draggableContainer);

  // Once video has fully loaded, draw the first frame to the canvas and display it
  video.onloadeddata = () => {    
    drawFirstVideoFrameToCanvas(video, canvas, draggableContainer);
  };

  // Add video event handlers
  addVideoEventHandlers(video, canvas);

  // If mobile, scroll down to video frame...otherwise, scroll down to position Design Options box at the top
  // and have as much of the preview frames in view as possible
  const isMobile = window.innerWidth <= 820; // Moved up from 768 to 820 to include the 7-inch 800 x 1280 tablets to display stacked as well
  if (isMobile) {
    const videoPreviewFrame = document.getElementById("video-preview");
    if (videoPreviewFrame) {
      const centeringOffset = 220;
      const scrollToPosition = videoPreviewFrame.getBoundingClientRect().top + window.pageYOffset - centeringOffset;
      window.scrollTo({
        top: scrollToPosition,
        behavior: "smooth"
      });
    }
  } else {
    const designOptionsBox = document.getElementById("design-options-container");
    if (designOptionsBox) {
      const navBarOffset = 120;
      const scrollToPosition = designOptionsBox.getBoundingClientRect().top + window.pageYOffset - navBarOffset;
      window.scrollTo({
        top: scrollToPosition,
        behavior: "smooth"
      });
    }  
  }

  // Display drag video hint (added an animation trigger reflow so it bounces with each new video added)  
  const hintElement = document.querySelector("#video-preview-wrapper .hint");
  hintElement.classList.remove("visible");
  void hintElement.offsetWidth;
  hintElement.classList.add("visible");
}

// Function to draw the first video frame to the canvas once video loads
function drawFirstVideoFrameToCanvas(video, canvas, draggableContainer) {

  // Match canvas and draggable container to scaled video size
  // This should happen after video has been scaled
  canvas.width = video.clientWidth;
  canvas.height = video.clientHeight;
  draggableContainer.style.width = video.clientWidth + "px";
  draggableContainer.style.height = video.clientHeight + "px";

  // Prepare ctx for use in drawFirstFrame function
  const ctx = canvas.getContext("2d");

  // Draw the first frame to the canvas and display it on load -- Different browsers have different
  // issues making sure the first frame is drawn -- This uses multiple techniques together including:
  // hard-setting seek time, making sure the video ready state is ready to play (>2), requesting
  // animation frames until ready, and setting a slight delay for Safari once all conditions pass
  video.currentTime = 0;
  function drawFirstFrame() {
    
    // Wait for video to be in ready state
    if (video.readyState >= 2) {
      
      // Small timeout for Safari to make sure frame is ready to load (increase time if there's still issues)
      if (isSafari) {
        setTimeout(function () {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
        }, 100);
      } else {
        // For non-Safari browsers, we can draw right away
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);        
      }

      // Draw video control icons for paused state and show canvas
      drawVideoControlIconsForState(VideoState.PAUSED);
      canvas.style.display = "block";
      video.style.display = "none";
    } else {      
      // If video ready state is not ready to play, request another frame
      requestAnimationFrame(drawFirstFrame);
    }
  }

  // Initial call to request animation frame using drawFirstFrame function defined above
  requestAnimationFrame(drawFirstFrame);
}

// Function to add the event handler functions for the video
function addVideoEventHandlers(video, canvas) {
  // Event listener for pausing the video
  video.addEventListener("pause", () => {
    
    if (video.paused) {
      // Getting and setting the current time fixes a stutter bug in Safari
      let currentVideoTime = video.currentTime;
      video.currentTime = currentVideoTime;

      // Set canvas size to match video's scaled dimensions
      canvas.width = video.clientWidth;
      canvas.height = video.clientHeight;

      // Putting this in a small timeout helps on iOS mobile devices where it takes the frame a longer time 
      // to load before drawing the canvas
      setTimeout(() => {
        // Draw the current frame to the canvas
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        video.style.display = "none";
        
      }, 200); // 200 ms timeout

      // Draw the video control icon for when video is paused, display canvas
      drawVideoControlIconsForState(VideoState.PAUSED);

      canvas.style.display = "block";
     
    }
  });

  // Event listener for playing the video
  video.addEventListener("play", () => {
    // Draw video control icons for when video is playing, display video
    drawVideoControlIconsForState(VideoState.PLAYING);
    canvas.style.display = "none"; // Hide the canvas
    video.style.display = "block"; // Show the video
  });

  // Event listener for when the video ends
  video.addEventListener("ended", () => {
    // Draw video control icons for when the video is paused, display canvas
    drawVideoControlIconsForState(VideoState.PAUSED);
    canvas.style.display = "none"; // Hide the canvas
    video.style.display = "block"; // Show the video
  });
}

// Function to scale down image and video element to fit within the preview frame
function scaleElement(element, targetWidth, targetHeight) {
  // Get the original dimensions of the element (image or video)
  const originalWidth = element.naturalWidth || element.videoWidth;
  const originalHeight = element.naturalHeight || element.videoHeight;

  // Calculate the aspect ratios
  const elementAspectRatio = originalWidth / originalHeight;
  const targetAspectRatio = targetWidth / targetHeight;

  // Determine if this is an image or video so we know which exact side fit to store later
  let isImage = element.tagName.toLowerCase() === "img";

  // Compare the aspect ratios and scale the element accordingly
  // Also store the exact fit side for our later cropping calculations
  let scaledWidth, scaledHeight;
  if (elementAspectRatio < targetAspectRatio) {
    // Element is wider compared to the frame
    element.style.width = targetWidth + "px";
    element.style.height = "auto";
    if (isImage) {
      exactFitSideImage = "width";
    } else {
      exactFitSideVideo = "width";
    }

    // Assign scaled width and height to scale other video elements
    scaledWidth = targetWidth;
    scaledHeight = scaledWidth / elementAspectRatio;
  } else {
    // Element is taller or equal compared to the frame
    element.style.height = targetHeight + "px";
    element.style.width = "auto";
    if (isImage) {
      exactFitSideImage = "height";
    } else {
      exactFitSideVideo = "height";
    }

    // Assign scaled width and height to scale other video elements
    scaledHeight = targetHeight;
    scaledWidth = scaledHeight * elementAspectRatio;
  }

  // For video element, we also need to scale draggableContainer, videoFrameCanvas, and iconCanvas
  if (!isImage) {
    const draggableContainer = document.querySelector("#draggable-container");
    const videoFrameCanvas = document.querySelector("#videoFrameCanvas");
    const iconCanvas = document.getElementById("iconCanvas");

    // Scale draggableContainer to match video size
    if (draggableContainer) {
      draggableContainer.style.width = scaledWidth + "px";
      draggableContainer.style.height = scaledHeight + "px";
    }

    // Scale videoFrameCanvas to match video size
    if (videoFrameCanvas) {
      videoFrameCanvas.width = scaledWidth;
      videoFrameCanvas.height = scaledHeight;
      const ctx = videoFrameCanvas.getContext("2d");
      const video = document.querySelector("#video-preview video");
      ctx.clearRect(0, 0, videoFrameCanvas.width, videoFrameCanvas.height);
      ctx.drawImage(
        video,
        0,
        0,
        videoFrameCanvas.width,
        videoFrameCanvas.height
      );
    }

    // Scale icon canvas to match the preview frame, which should be targetWidth and targetHeight
    if (iconCanvas) {
      iconCanvas.width = targetWidth;
      iconCanvas.height = targetHeight;

      // Get the transition duration from CSS if one is set
      const previewWindow = document.querySelector(".preview-window");
      const previewWindowComputedStyle = window.getComputedStyle(previewWindow);
      const transitionDuration = previewWindowComputedStyle.transitionDuration;
      let transitionDurationsMilliseconds =
        parseFloat(transitionDuration) * 1000;
      if (isNaN(transitionDurationsMilliseconds)) {
        transitionDurationsMilliseconds = 0; // Default value
      }

      // Redraw video control buttons to reposition with new scale
      setTimeout(() => {
        if (currentVideoControlIcons === VideoControlIconsSet.PLAYING) {
          drawVideoControlIconsForState(VideoState.PLAYING);
        } else {
          drawVideoControlIconsForState(VideoState.PAUSED);
        }
      }, transitionDurationsMilliseconds);
    }
  } else {
    // For image element, we also need to scale image container
    const imageContainer = document.getElementById("image-container");

    // Scale draggableContainer to match video size
    if (imageContainer) {
      imageContainer.style.width = scaledWidth + "px";
      imageContainer.style.height = scaledHeight + "px";
    }
  }
}

// Function to change the aspect ratio of the preview frames
function changeAspectRatio(ratio) {
  // Note: Page will initially load without calling this function and on some medium screen sizes in between mobile size and
  // desktop size, it could allow the initial square frames to be a little larger than they would be compared to when this 
  // method is called. This is fine because it gives the square frames a larger visual size on initial page load, and it 
  // does not cause any problems -- Once an aspect ratio button is clicked it will cause this method to be called, which
  // sizes the frames based on a width that can fit two landscape frames, and thus going back to square frames on medium
  // size screens could cause the frames to be a little smaller than their size on initial page load -- This isn't a real
  // issue, but maybe in the future, we update this function to resize square and portrait frames so they take up maximum
  // (or ideal) visual space instead of just having square and landscape heights be controlled by the height that can
  // also fit the width of two landscape frames -- This note is here to explain this visual issue in case I notice it
  // again later and need to remember why this is happening
  
  // If aspect ratio changed from previous selection then reset Done with Image and Video buttons and
  // hide the Order Confirmation button and Canvas Name Exp section until Done with Image and Video buttons are confirmed again
  if (ratio !== selectedAspectRatio) {
    wasDonePositioningButtonPressed = false;
    document.getElementById("order-confirmation-section").style.display =
      "none";
    document.getElementById("canvas-name-section").style.display = "none";
  }

  // Assign the corrected button id as the "selected" one for CSS visual to denote the current selection to the user
  const aspectRatioButtons = document.querySelectorAll('.aspect-ratio-button-container button');
  aspectRatioButtons.forEach(button => button.classList.remove('selected'));
  if (ratio === AspectRatio.SQUARE) {
    document.getElementById("aspect-square").classList.add('selected');
  } else if (ratio === AspectRatio.LANDSCAPE) {
    document.getElementById("aspect-landscape").classList.add('selected');
  } else if (ratio === AspectRatio.PORTRAIT) {
    document.getElementById("aspect-portrait").classList.add('selected');
  }

  // Set max widths and padding based on device type
  const mobileMaxWidth = 480;
  const desktopMaxWidth = 720;
  const mobilePadding = 60;
  const dekstopPadding = 140;

  // Check if mobile so we can adjust the frame dimensions properly
  const isMobile = window.innerWidth <= 820; // Moved up from 768 to 820 to include the 7-inch 800 x 1280 tablets to display stacked as well
  const mobileWidth = Math.min(window.innerWidth - mobilePadding, mobileMaxWidth);

  // If Desktop, adjust frame width based on screen size to ensure frames fit side-by-side
  const desktopWidth = Math.min((window.innerWidth - dekstopPadding) / 2, desktopMaxWidth);

  let newWidth, newHeight;
  // Update dimensions based on the selected ratio
  if (ratio === AspectRatio.SQUARE) {    
    if (isMobile) {
      // Scale for mobile
      newWidth = mobileWidth;
      newHeight = newWidth;
    } else {
      // Scale for desktop
      newWidth = desktopWidth / 1.5;
      
      newHeight = desktopWidth / 1.5;
    }
  } else if (ratio === AspectRatio.LANDSCAPE) {
    if (isMobile) {
      // Scale for mobile
      newWidth = mobileWidth;
      newHeight = newWidth / 1.5;
    } else {
      // Scale for desktop
      newWidth = desktopWidth;
      newHeight = desktopWidth / 1.5;
    }
  } else if (ratio === AspectRatio.PORTRAIT) {
    if (isMobile) {
      // Scale for mobile
      newWidth = mobileWidth;
      newHeight = mobileWidth * 1.5;
    } else {
      // Scale for desktop
      newWidth = desktopWidth / 1.5;
      newHeight = desktopWidth;
    }
  }

  // Store the selected aspect ratio for later cropping calculations
  selectedAspectRatio = ratio;

  // Get the preview windows
  const imagePreviewWindow = document.querySelector("#image-preview");
  const videoPreviewWindow = document.querySelector("#video-preview");

  // Reset the position of the image and video to the upper left corner
  const imageElement = document.querySelector("#image-container img");
  const videoElement = document.querySelector("#video-preview video");
  resetElementPosition(imageElement);
  resetElementPosition(videoElement);

  // Update the dimensions of the preview windows
  imagePreviewWindow.style.width = newWidth + "px";
  imagePreviewWindow.style.height = newHeight + "px";
  videoPreviewWindow.style.width = newWidth + "px";
  videoPreviewWindow.style.height = newHeight + "px";

  // Re-scale the content inside these preview windows
  // Draggable container, video frame canvas, and icon canvas will be scaled
  // along with video element in the scaleElement function
  if (imageElement) {
    scaleElement(imageElement, newWidth, newHeight);
  }
  if (videoElement) {
    scaleElement(videoElement, newWidth, newHeight);
  }
}

// Resets the position of an element to the upper left-hand corner
function resetElementPosition(element) {
  if (element) {
    // Reset element to top left position
    element.style.left = "0px";
    element.style.top = "0px";

    // If it's the video, also reset position of draggable container and video fram canvas
    if (element.tagName.toLowerCase() === "video") {
      const draggableContainer = document.querySelector("#draggable-container");
      const videoFrameCanvas = document.querySelector("#videoFrameCanvas");
      draggableContainer.style.left = "0px";
      draggableContainer.style.top = "0px";
      videoFrameCanvas.style.left = "0px";
      videoFrameCanvas.style.top = "0px";
    }
  }
}

function updateCanvasSizeDropdownOptionsDisplay() {
  const canvasSizeDropdown = document.getElementById("canvas-size-select");
  let sizeOptions = [];

  // Set what the dropdown should display for each option based on the current aspect ratio
  switch (selectedAspectRatio) {
    case AspectRatio.SQUARE:
      sizeOptions = [
        '12"x12" • Small',
        '16"x16" • Medium',
        '24"x24" • Large',
      ];
      break;
    case AspectRatio.PORTRAIT:
      sizeOptions = [
        '12"x18" • Small',
        '16"x24" • Medium',
        '24"x36" • Large',
      ];
      break;
    case AspectRatio.LANDSCAPE:
      sizeOptions = [
        '18"x12" • Small',
        '24"x16" • Medium',
        '36"x24" • Large',
      ];
      break;
  }

  // Repopulate the dropdown display using innerHTML and set the dropdown value to the currently selected canvas size
  canvasSizeDropdown.innerHTML = sizeOptions
    .map(
      (size, index) =>
        `<option value="${Object.values(CanvasSize)[index]}" ${
          selectedCanvasSize === Object.values(CanvasSize)[index]
            ? "selected"
            : ""
        }>${size}</option>`
    )
    .join("");
}

// Function to update the canvas price based on the currently selected canvas size
function getCanvasPrice() {
  // Set up prices based based on global enums and to match using the values for selectedCanvasSize
  const prices = {
    small: CanvasPrice.SMALL,
    medium: CanvasPrice.MEDIUM,
    large: CanvasPrice.LARGE,
    "x-large": CanvasPrice.X_LARGE,
  };

  // Get price based on selectedCanvasSize
  const currentPrice = prices[selectedCanvasSize];

  // Return price
  return currentPrice;
}

// Function to resize the QR code sample image when the selected canvas size changes
function resizeQrCodeSampleImage() {

  // Get selected canvas width
  const { canvasWidth, canvasHeight } = getCanvasDimensions(selectedAspectRatio, selectedCanvasSize);

  // Get the dimensions of the preview window dynamically
  const previewWindow = document.querySelector("#image-preview");
  const previewWindowWidth = previewWindow.clientWidth;
  const previewWindowHeight = previewWindow.clientHeight;

  // Calculate the QR code size in pixels
  const qrCodeWidthInInches = 2;
  const qrCodeWidthRatio = qrCodeWidthInInches / canvasWidth;
  const qrCodeWidthInPixels = previewWindowWidth * qrCodeWidthRatio;  

// Calculate the margin in pixels
  const qrCodeMarginInInches = 0.5;
  const qrCodeMarginInPixelsBottom = (qrCodeMarginInInches / canvasHeight) * previewWindowHeight;
  const qrCodeMarginInPixelsRight = (qrCodeMarginInInches / canvasWidth) * previewWindowWidth;

  // Set the width and calculate the height based on the aspect ratio
  const qrCodeImage = document.getElementById('qr-code-sample-image');
  const qrCodeOriginalWidth = qrCodeImage.naturalWidth;
  const qrCodeOriginalHeight = qrCodeImage.naturalHeight;
  const qrCodeAspectRatio = qrCodeOriginalHeight / qrCodeOriginalWidth;

  // Set the width and height of the QR code image
  qrCodeImage.style.width = `${qrCodeWidthInPixels}px`;
  qrCodeImage.style.height = `${qrCodeWidthInPixels * qrCodeAspectRatio}px`;  

  // Set the position of the QR code image
  qrCodeImage.style.bottom = `${qrCodeMarginInPixelsBottom}px`;
  qrCodeImage.style.right = `${qrCodeMarginInPixelsRight}px`;
}

// Function to show the popup message -- Takes in bool signifying if the call is coming from the initial upload or not
function showPoorImageTargetPopupMessage(isCheckForInitialUpoad) {
  const popup = document.getElementById("poor-image-target-popup-message");

  let poorImageTargetMessage;
  if (isCheckForInitialUpoad) {

    // Popup message if check is for the initial upload
    poorImageTargetMessage=
      `Uh-oh!

      For a great AR experience, images need a minimum level of detail, edges, and contrast. Monotone, gradient, or low-detail images may not work well.

      Please choose another image with a little more detail, edges, or contrast.
      `;

    // Set popup data for clearImage to be true so that image is cleared when the user closes the popup message
    popup.dataset.clearImage = "true";
    
  } else {

    // Popup message if it is the second check of the visible repositioned image
    poorImageTargetMessage=
      `Uh-oh!

      For a great AR experience, images need a minimum level of detail, edges, and contrast. Monotone, gradient, or low-detail images may not work well.

      Please reposition this image to show a little more detail, edges, or contrast. Or choose another image.
      `;

    // Set popup data for clearImage to be true so that image is cleared when the user closes the popup message
    popup.dataset.clearImage = "false";
  }
  
  // Replace newline characters with <br> tags
  const formattedMessage = poorImageTargetMessage.replace(/\n/g, '<br>');
  
  // Set the popup message content as HTML
  const popupText = document.getElementById("popup-poor-image-target-text");
  popupText.innerHTML = formattedMessage;
  popup.classList.remove("hidden");

  // Add a slight delay to ensure the opacity transition takes effect
  setTimeout(() => {
    popup.style.opacity = "1";
  }, 10);
}

// Function to hide the popup message
function hidePoorImageTargetPopupMessage() {
  const popup = document.getElementById("poor-image-target-popup-message");
  popup.style.opacity = "0";
  setTimeout(() => {
    popup.classList.add("hidden");

    // Clear the displayed image if the clearImage flag was set to true
    if (popup.dataset.clearImage === "true") {
      const previewWindow = document.querySelector("#image-container"); 
      previewWindow.innerHTML = ' '; // Space added so that the preview window frame does not disappear as well
      imageUploadFile = null;
      isImageUploaded = false;      
    }
  }, 300); // Match the duration of the opacity transition
}

// Function to check if both image and video are uploaded and display buttons for next step
function checkBothUploaded() {
  if (isImageUploaded && isVideoUploaded) {
    document.getElementById("done-positioning-button").style.display = "block";

    // The next section will help center the preview frames for positioning once both image and video are uploaded
    // However, we are commenting it out for now as the user may not have finished with the other settings in the
    // options box -- But we may use this later once we implement a settings confirmation section
    /*
    const previewFrameElements = document.getElementById("image-preview-wrapper");
    if (previewFrameElements) {
      const navBarOffset = 160; // Offset to compensate for navigation bar
      const elementPosition = previewFrameElements.getBoundingClientRect().top + window.pageYOffset - navBarOffset;
      window.scrollTo({
        top: elementPosition,
        behavior: "smooth"
      });
      //donePositioningButton.scrollIntoView({behaviour: "smooth"});
    }
    */

    // Commented out below because we are not outputting this information on the live site
    // document.getElementById("output-section").style.display = "block";
  }
}

// Function to check if both Done with Image and Done with Video buttons were pressed so we can display
// Canvas Name Experience section and Order Confirmartion button
function checkBothDoneButtonsPressed() {
  // If both Done buttons were pressed show canvas name experience section
  //
  // We have changed this to just one Done Positioning button
  if (wasDonePositioningButtonPressed) {
    document.getElementById("canvas-name-section").style.display = "block";

    // Scroll to Name Your Canvas Experience section
    const elementToScrollTo = document.getElementById("canvas-name-section");
    if (elementToScrollTo) {
      const elementToScrollToRect = elementToScrollTo.getBoundingClientRect();
      const elementToScrollToPosition = elementToScrollToRect.top + window.pageYOffset;
      const offsetPosition = elementToScrollToPosition - (window.innerHeight / 2) + (elementToScrollToRect.height / 2);

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }    

    // If canvas experience name was added then show Order Confirmation button as well
    if (wasCanvasExperienceNameAdded) {
      document.getElementById("order-confirmation-section").style.display =
        "block";
    }
  }
}

// Function to draw the video control icons based on whether video is playing or paused
function drawVideoControlIconsForState(videoState) {
  // Clear previous icons, make sure canvas is visible and appropriately sized
  const iconCanvas = document.getElementById("iconCanvas");
  const ctx = iconCanvas.getContext("2d");
  ctx.clearRect(0, 0, iconCanvas.width, iconCanvas.height);
  iconCanvas.style.display = "block";
  iconCanvas.width = document.getElementById("video-preview").clientWidth;
  iconCanvas.height = document.getElementById("video-preview").clientHeight;

  // Clear previous buttons before drawing the new ones
  const container = iconCanvas.parentElement;
  const existingButtons = container.querySelectorAll(".video-control-buttons");
  existingButtons.forEach((button) => {
    container.removeChild(button);
  });

  // Get video element to pass into functions below
  const videoElement = document.querySelector("#video-preview video");

  // Icon buttons to draw and create based on whether video is playing or paused
  if (videoState === VideoState.PLAYING) {
    // When video is playing, draw pause and reset icon buttons
    drawPauseIcon(-50); // xOffset for play icon
    drawResetIcon(50); // xOffset for reset icon

    // Overlay a transparent button to make pause icon an operable button
    createTransparentButton("pauseButton", iconBounds.pause, function () {
      if (videoElement) {
        videoElement.pause();
      }
    });

    // Overlay a transparent button to make reset icon an operable button
    createTransparentButton("resetButton", iconBounds.reset, function () {
      if (videoElement) {
        // Seek to frame 0, play-then-pause forces pause eventListener so frame can be drawn
        videoElement.currentTime = 0;
        videoElement.play().then(() => {
          videoElement.pause();
        });
      }
    });

    // Set current video control icons state to video playing icon set
    currentVideoControlIcons = VideoControlIconsSet.PLAYING;
  } else if (videoState === VideoState.PAUSED) {
    // When video is paused, draw play and reset icon buttons
    drawPlayIcon(-50); // xOffset for pause icon
    drawResetIcon(50); // xOffset for reset icon

    // Overlay a transparent button to make play icon an operable button
    createTransparentButton("playButton", iconBounds.play, function () {
      if (videoElement) {
        videoElement.play();
      }
    });

    // Overlay a transparent button to make reset icon an operable button
    createTransparentButton("resetButton", iconBounds.reset, function () {
      if (videoElement) {
        // Seek to frame 0, play-then-pause forces pause eventListener so frame can be drawn
        videoElement.currentTime = 0;
        videoElement.play().then(() => {
          videoElement.pause();
        });
      }
    });

    // Set current video control icons state to video paused icon set
    currentVideoControlIcons = VideoControlIconsSet.PAUSED;
  }
}

// Function to draw the play icon
function drawPlayIcon(xOffset) {
  const iconCanvas = document.getElementById("iconCanvas");
  const ctx = iconCanvas.getContext("2d");

  // Adjust these sizes as needed
  const size = 30;
  const iconBackgroundRadius = 40;

  // Get center of where the icon will be placed
  const centerX = iconCanvas.width / 2 + xOffset;
  const centerY = iconCanvas.height / 2;

  // Draw the semi-transparent background circle of the icon
  ctx.beginPath();
  ctx.arc(centerX, centerY, iconBackgroundRadius, 0, 2 * Math.PI, false);
  ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
  ctx.fill();

  // Draw the play triangle
  const triangleOffset = 6;
  ctx.beginPath();
  ctx.moveTo(centerX - size / 2 + triangleOffset, centerY - size / 2);
  ctx.lineTo(centerX + size / 2 + triangleOffset, centerY);
  ctx.lineTo(centerX - size / 2 + triangleOffset, centerY + size / 2);
  ctx.fillStyle = "rgba(255, 255, 255, 1.0)";
  ctx.fill();
  ctx.closePath();

  // Update the icons bounds for this icon's button
  iconBounds.play = { x: centerX, y: centerY, radius: iconBackgroundRadius };
}

// Function to draw the reset icon
function drawResetIcon(xOffset) {
  const iconCanvas = document.getElementById("iconCanvas");
  const ctx = iconCanvas.getContext("2d");

  // Adjust these sizes as needed
  const size = 30;
  const iconBackgroundRadius = 40;

  // Get center of where the icon will be placed
  const centerX = iconCanvas.width / 2 + xOffset;
  const centerY = iconCanvas.height / 2;

  // Draw the semi-transparent background circle of the icon
  ctx.beginPath();
  ctx.arc(centerX, centerY, iconBackgroundRadius, 0, 2 * Math.PI);
  ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
  ctx.fill();

  // Arrow parameters
  const arrowRadius = size / 2;
  const arrowStartAngle = Math.PI * 3.0; // Start at 9 o'clock
  const arrowEndAngle = Math.PI * 1.3; // End between 12 and 9 o'clock

  // Draw the circular part of the arrow
  ctx.beginPath();
  ctx.arc(centerX, centerY, arrowRadius, arrowStartAngle, arrowEndAngle, true);
  ctx.lineWidth = 4;
  ctx.strokeStyle = "rgba(255, 255, 255, 1.0)";
  ctx.stroke();

  // Calculate base coordinates and adjust rotation
  const baseWidth = 11;
  const desiredRotation = 0.1; // Adjust this value as needed
  const adjustedBaseAngle = arrowEndAngle + desiredRotation;
  const baseX =
    centerX + (arrowRadius - baseWidth / 2) * Math.cos(adjustedBaseAngle);
  const baseY =
    centerY + (arrowRadius - baseWidth / 2) * Math.sin(adjustedBaseAngle);

  // Calculate the left and right  points of the base of the triangle
  const leftX = baseX;
  const leftY = baseY;
  const rightX = baseX + baseWidth * Math.cos(adjustedBaseAngle);
  const rightY = baseY + baseWidth * Math.sin(adjustedBaseAngle);

  // Calculate midpoint of the base
  const midX = (baseX + (baseX + baseWidth * Math.cos(adjustedBaseAngle))) / 2;
  const midY = (baseY + (baseY + baseWidth * Math.sin(adjustedBaseAngle))) / 2;

  // Calculate the height of the equilateral triangle
  const height = (Math.sqrt(3) / 2) * baseWidth;

  // Direction vector for the height from the midpoint, considering the perpendicular direction
  const heightDirX = height * Math.cos(adjustedBaseAngle + Math.PI / 2);
  const heightDirY = height * Math.sin(adjustedBaseAngle + Math.PI / 2);

  // Calculate the tip point
  const tipX = midX - heightDirX;
  const tipY = midY - heightDirY;

  // Draw the triangle
  ctx.beginPath();
  ctx.moveTo(leftX, leftY); // Start at the left base point
  ctx.lineTo(rightX, rightY); // Go to the right base point
  ctx.lineTo(tipX, tipY); // Connect to the top point
  ctx.lineTo(leftX, leftY); // Close the triangle
  ctx.fillStyle = "rgba(255, 255, 255, 1.0)";
  ctx.fill();

  // Update the icons bounds for this icon's button
  iconBounds.reset = { x: centerX, y: centerY, radius: iconBackgroundRadius };
}

// Function to draw the pause icon
function drawPauseIcon(xOffset, shouldFade) {
  const iconCanvas = document.getElementById("iconCanvas");
  const ctx = iconCanvas.getContext("2d");

  // Adjust these sizes as needed
  const size = 30;
  const iconBackgroundRadius = 40;

  // Variables to assist in fade in
  //const duration = 30; // Duration of the fade-in effect in milliseconds
  //const frameRate = 60; // Number of frames per second
  //const totalFrames = (duration / 1000) * frameRate; // Total number of frames
  //let currentFrame = 0; // Current frame counter
  //let opacity = 0; // Initial opacity

  // Get center of where the icon will be placed
  const centerX = iconCanvas.width / 2 + xOffset;
  const centerY = iconCanvas.height / 2;

  // Draw the semi-transparent background circle of the icon
  ctx.beginPath();
  ctx.arc(centerX, centerY, iconBackgroundRadius, 0, 2 * Math.PI, false);
  ctx.fillStyle = "rgba(255, 255, 255, 0.2)";
  ctx.fill();

  // Pause icon rectangles values
  const rectHeight = size; // Height of each rectangle
  const rectWidth = size / 4; // Width of each rectangle
  const gap = 6; // Gap between rectangles
  const x = centerX - (rectWidth + gap / 2);
  const y = centerY - rectHeight / 2;

  // Draw the pause icon rectangles
  ctx.beginPath();
  ctx.rect(x, y, rectWidth, rectHeight);
  ctx.rect(x + rectWidth + gap, y, rectWidth, rectHeight);
  ctx.fillStyle = "rgba(255, 255, 255, 1.0)";
  ctx.fill();
  ctx.closePath();

  // Update the icons bounds for this icon's button
  iconBounds.pause = { x: centerX, y: centerY, radius: iconBackgroundRadius };
}

// Function to create and position a transparent button over an icon
function createTransparentButton(iconId, iconBounds, onClickCallback) {
  // Calculate the dimensions and position based on icon bounds
  const width = iconBounds.radius * 2;
  const height = width;
  const top = iconBounds.y - iconBounds.radius;
  const left = iconBounds.x - iconBounds.radius;

  // Create the button element
  const button = document.createElement("button");
  button.className = "video-control-buttons"; // Class used for when we need to remove all buttons canvas
  button.id = iconId;
  button.style.position = "absolute";
  button.style.top = top + "px";
  button.style.left = left + "px";
  button.style.width = width + "px";
  button.style.height = height + "px";
  button.style.opacity = "0"; // Make button transparent
  button.style.cursor = "pointer"; // Change cursor to pointer to indicate it's clickable
  button.style.border = "none"; // Remove border
  button.style.background = "none"; // Remove background
  button.style.outline = "none"; // Remove outline to avoid focus outline

  // Add click event listener
  button.addEventListener("click", onClickCallback);

  // Append the button to the container that holds your canvas
  const container = document.getElementById("iconCanvas").parentElement;
  container.appendChild(button);
}

// Function to make an element draggable
function makeDraggable(element) {
  let offsetX, offsetY, initialX, initialY;
  let isDragging = false;
  let hasMoved = false;

  // Get padding so we can subtract that later from clientWidth to get the exact inner element dimensions of frame
  const imagePreviewWindow = document.querySelector("#image-preview");
  const imagePreviewComputedStyle = window.getComputedStyle(imagePreviewWindow);
  const imagePreviewPadding =
    parseFloat(imagePreviewComputedStyle.paddingLeft) || 0;

  // Function to handle mouse move
  function handleMove(e) {
    //function handleMouseMove(e) {
    e.preventDefault();
    if (isDragging) {
      // Added to combine touch events to mouse events
      const clientX = e.type.includes("touch")
        ? e.touches[0].clientX
        : e.clientX;
      const clientY = e.type.includes("touch")
        ? e.touches[0].clientY
        : e.clientY;
      const dx = clientX - initialX;
      const dy = clientY - initialY;

      // Check if element has moved and, if so, reset element isDone button and hide Order Confirmation button
      // and Canvas Name Exeprience section
      if (dx !== 0 || (dy !== 0 && !hasMoved)) {
        hasMoved = true;

        // Get element and reset its isDone button
        const imageElement = document.querySelector("#image-preview img");
        const draggableContainerElement = document.querySelector(
          "#draggable-container"
        );
        if (element === imageElement) {
          wasDonePositioningButtonPressed = false;
        } else if (element === draggableContainerElement) {
          wasDonePositioningButtonPressed = false;
        }

        // Hide Order Confirmation button and Canvas Name Exp section so user needs to confirm when image or video movement is done again
        document.getElementById("order-confirmation-section").style.display =
          "none";
        document.getElementById("canvas-name-section").style.display = "none";
      }

      // Change to incorporate the above line that adds touch events
      const newLeft = offsetX + dx;
      const newTop = offsetY + dy;
      const elementWidth = element.offsetWidth;
      const elementHeight = element.offsetHeight;
      const frameWidth =
        imagePreviewWindow.clientWidth - imagePreviewPadding * 2;
      const frameHeight =
        imagePreviewWindow.clientHeight - imagePreviewPadding * 2;

      // Check for restrictions before moving and hard code edge case if we are past it
      if (newLeft > 0) {
        element.style.left = "0px";
      } else if (newLeft < frameWidth - elementWidth) {
        element.style.left = frameWidth - elementWidth + "px";
      } else {
        element.style.left = newLeft + "px";
      }

      if (newTop > 0) {
        element.style.top = "0px";
      } else if (newTop < frameHeight - elementHeight) {
        element.style.top = frameHeight - elementHeight + "px";
      } else {
        element.style.top = newTop + "px";
      }
    }
  }

  // Unified end handler for both mouse and touch
  function handleEnd(e) {
    e.preventDefault();
    isDragging = false;
    document.removeEventListener("mousemove", handleMove);
    document.removeEventListener("mouseup", handleEnd);
    document.removeEventListener("touchmove", handleMove);
    document.removeEventListener("touchend", handleEnd);
  }

  // When mouse is pressed down
  element.addEventListener("mousedown", (e) => {
    e.preventDefault();
    initialX = e.clientX;
    initialY = e.clientY;
    offsetX = element.offsetLeft;
    offsetY = element.offsetTop;
    isDragging = true;

    // Changed to incorporate the new handleMov and handleEnd functions
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    //document.addEventListener("mousemove", handleMouseMove);
    //document.addEventListener("mouseup", handleMouseUp);
  });

  // Added function for mobile touch
  element.addEventListener("touchstart", (e) => {
    e.preventDefault(); // Optionally, prevent the window from scrolling
    // For touch events, use the first touch point
    initialX = e.touches[0].clientX;
    initialY = e.touches[0].clientY;
    offsetX = element.offsetLeft;
    offsetY = element.offsetTop;
    isDragging = true;

    document.addEventListener("touchmove", handleMove, { passive: false });
    document.addEventListener("touchend", handleEnd);
  });
}

// Function to handle 'I'm Done with Image' button click
function handleImageDone() {  
  const imageElement = document.querySelector("#image-container img");
  if (!imageElement) {
    alert("No image uploaded.");
    return;
  }

  // Get the current position of the image
  const left = parseInt(imageElement.style.left, 10) || 0;
  const top = parseInt(imageElement.style.top, 10) || 0;

  // Original dimensions of the image
  const originalWidth = imageElement.naturalWidth;
  const originalHeight = imageElement.naturalHeight;

  // Get the scaled dimensions
  const scaledWidth = imageElement.offsetWidth;
  const scaledHeight = imageElement.offsetHeight;

  // Get the computed style of the image parent (preview window) so we can get the padding information
  // Then use client properties, which exclude border, and just subtract the padding to get the
  // frame dimensions
  const imageParentElement = imageElement.parentElement;
  const imageParentComputedStyle = window.getComputedStyle(imageParentElement);
  const paddingTop = parseFloat(imageParentComputedStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(imageParentComputedStyle.paddingBottom) || 0;
  const paddingLeft = parseFloat(imageParentComputedStyle.paddingLeft) || 0;
  const padddingRight = parseFloat(imageParentComputedStyle.paddingRight) || 0;
  const clientWidth = imageParentElement.clientWidth;
  const clientHeight = imageParentElement.clientHeight;
  const frameWidth = clientWidth - paddingLeft - padddingRight;
  const frameHeight = clientHeight - paddingTop - paddingBottom;

  // Calculate the scale factor
  const scaleX = originalWidth / scaledWidth;
  const scaleY = originalHeight / scaledHeight;

  // Convert pixel values to original unscaled element and change negative drag offsets to positive crop start positions and round down
  const originalLeft = Math.floor(Math.abs(left * scaleX));
  const originalTop = Math.floor(Math.abs(top * scaleY));

  // Use selected aspect ratio and exact fit side to calculate what the crop width and crop height should be
  let cropWidth, cropHeight;
  switch (selectedAspectRatio) {
    case AspectRatio.SQUARE:
      if (exactFitSideImage === "width") {
        cropWidth = originalWidth;
        cropHeight = originalWidth;
      } else {
        cropHeight = originalHeight;
        cropWidth = originalHeight;
      }
      break;
    case AspectRatio.LANDSCAPE:
      if (exactFitSideImage === "width") {
        cropWidth = originalWidth;
        cropHeight = originalWidth / 1.5;
      } else {
        cropHeight = originalHeight;
        cropWidth = originalHeight * 1.5;
      }
      break;
    case AspectRatio.PORTRAIT:
      if (exactFitSideImage === "width") {
        cropWidth = originalWidth;
        cropHeight = originalWidth * 1.5;
      } else {
        cropHeight = originalHeight;
        cropWidth = originalHeight / 1.5;
      }
      break;
  }

  // Round the cropping numbers in case the 1.5 factor created a decimal
  cropWidth = Math.round(cropWidth);
  cropHeight = Math.round(cropHeight);

  // Ensure the numbers are within bounds of the original dimensions
  let cropEndX = originalLeft + cropWidth;
  let cropEndY = originalTop + cropHeight;
  if (cropEndX > originalWidth) {
    cropWidth -= cropEndX - originalWidth;
  }
  if (cropEndY > originalHeight) {
    cropHeight -= cropEndY - originalHeight;
  }

  // Ensure the numbers are even
  cropWidth = cropWidth % 2 === 0 ? cropWidth : cropWidth - 1;
  cropHeight = cropHeight % 2 === 0 ? cropHeight : cropHeight - 1;

  // For square aspect ratio match the crop width and height if slightly off
  if (selectedAspectRatio === AspectRatio.SQUARE) {
    cropWidth = cropHeight = Math.min(cropWidth, cropHeight);
  }

  // Display the cropping info based on the calculated values
  /*
  displayCroppingInfo(
    originalTop,
    originalLeft,
    originalWidth,
    originalHeight,
    cropWidth,
    cropHeight,
    "Image"
  );
  */

  // Store cropping data in global imageCroppingData object
  imageCroppingData.imageUploadFile = imageUploadFile;
  imageCroppingData.originalWidth = originalWidth;
  imageCroppingData.originalHeight = originalHeight;
  imageCroppingData.originalLeft = originalLeft;
  imageCroppingData.originalTop = originalTop;
  imageCroppingData.cropWidth = cropWidth;
  imageCroppingData.cropHeight = cropHeight;
}

// Function to draw the image in the preview window on a canvas (used so we can evalute it as an image target)
function getVisiblePortionOfImage() {
  
  return new Promise((resolve, reject) => {
    const previewWindow = document.getElementById("image-preview");
    //const img = previewWindow.querySelector("#img");
    const img  = document.querySelector("#image-container img");
    const imageTargetEvalutationCanvas = document.getElementById("evaluate-image-target-canvas");
    const ctx = imageTargetEvalutationCanvas.getContext("2d");

    // Get the computed styles for the preview window to account for padding and border
    const previewStyle = window.getComputedStyle(previewWindow);
    const paddingLeft = parseFloat(previewStyle.paddingLeft);
    const paddingRight = parseFloat(previewStyle.paddingRight);
    const paddingTop = parseFloat(previewStyle.paddingTop);
    const paddingBottom = parseFloat(previewStyle.paddingBottom);
    const borderLeft = parseFloat(previewStyle.borderLeftWidth);
    const borderRight = parseFloat(previewStyle.borderRightWidth);
    const borderTop = parseFloat(previewStyle.borderTopWidth);
    const borderBottom = parseFloat(previewStyle.borderBottomWidth);

    // Calculate the effective width and height of the preview window minus padding
    const previewWidth = previewWindow.clientWidth - paddingLeft - paddingRight;
    const previewHeight = previewWindow.clientHeight - paddingTop - paddingBottom;

    // Set canvas dimensions to match the effective preview window dimensions
    imageTargetEvalutationCanvas.width = previewWidth;
    imageTargetEvalutationCanvas.height = previewHeight;

    // Calculate the scaling factor of the image
    const imgNaturalWidth = img.naturalWidth;
    const imgNaturalHeight = img.naturalHeight;
    const imgDisplayedWidth = img.width;
    const imgDisplayedHeight = img.height;

    const scaleX = imgNaturalWidth / imgDisplayedWidth;
    const scaleY = imgNaturalHeight / imgDisplayedHeight;
    
    // Calculate the position of the image within the preview window
    const imgRect = img.getBoundingClientRect();
    const previewRect = previewWindow.getBoundingClientRect();

    const x = (previewRect.left - imgRect.left - paddingLeft + borderLeft) * scaleX;
    const y = (previewRect.top - imgRect.top - paddingTop + borderTop) * scaleY;

    // Draw the visible portion of the image onto the canvas
    ctx.drawImage(img, x, y, previewWidth * scaleX, previewHeight * scaleY, 0, 0, previewWidth, previewHeight);

    // Create a new Image() object from the canvas content
    const visiblePortionImage = new Image();
    visiblePortionImage.src = imageTargetEvalutationCanvas.toDataURL();

    // Ensure image is loaded before returning it
    visiblePortionImage.onload = function() {
      resolve(visiblePortionImage);
    };

    // Error handling for image loading
    visiblePortionImage.onerror = function() {
      reject(new Error("Failed to load image from preview window for image target evaluation"));
    };
  });
}

// Function to handle 'I'm Done with Video' button click
function handleVideoDone() {
  // For this function, assign the video element to draggable container because
  // either the video or the video frame canvas is hidden
  const video = document.querySelector("#video-preview video");
  const videoElement = document.querySelector("#draggable-container");

  if (!video) {
    alert("No video uploaded.");
    return;
  }

  // Get the current position of the video
  const left = parseInt(videoElement.style.left, 10) || 0;
  const top = parseInt(videoElement.style.top, 10) || 0;

  // Original dimensions of the video -- Use the video object for this, not the draggable container
  const originalWidth = video.videoWidth;
  const originalHeight = video.videoHeight;

  // Get video scaled dimensions
  const scaledWidth = videoElement.offsetWidth;
  const scaledHeight = videoElement.offsetHeight;

  // Get the computed style of the video preview window so we can get the padding information
  // Then use client properties, which exclude border, and just subtract the padding to get the
  // frame dimensions
  const videoParentElement = document.getElementById("video-preview");
  const videoParentComputedStyle = window.getComputedStyle(videoParentElement);
  const paddingTop = parseFloat(videoParentComputedStyle.paddingTop) || 0;
  const paddingBottom = parseFloat(videoParentComputedStyle.paddingBottom) || 0;
  const paddingLeft = parseFloat(videoParentComputedStyle.paddingLeft) || 0;
  const padddingRight = parseFloat(videoParentComputedStyle.paddingRight) || 0;
  const clientWidth = videoParentElement.clientWidth;
  const clientHeight = videoParentElement.clientHeight;
  const frameWidth = clientWidth - paddingLeft - padddingRight;
  const frameHeight = clientHeight - paddingTop - paddingBottom;

  // Calculate the scale factor
  const scaleX = originalWidth / scaledWidth;
  const scaleY = originalHeight / scaledHeight;

  // Convert pixel values to original unscaled element and change negative drag offsets to positive crop start positions and round down
  const originalLeft = Math.floor(Math.abs(left * scaleX));
  const originalTop = Math.floor(Math.abs(top * scaleY));

  // Use selected aspect ratio and exact fit side to calculate what the crop width and crop height should be
  let cropWidth, cropHeight;
  switch (selectedAspectRatio) {
    case AspectRatio.SQUARE:
      if (exactFitSideVideo === "width") {
        cropWidth = originalWidth;
        cropHeight = originalWidth;
      } else {
        cropHeight = originalHeight;
        cropWidth = originalHeight;
      }
      break;
    case AspectRatio.LANDSCAPE:
      if (exactFitSideVideo === "width") {
        cropWidth = originalWidth;
        cropHeight = originalWidth / 1.5;
      } else {
        cropHeight = originalHeight;
        cropWidth = originalHeight * 1.5;
      }
      break;
    case AspectRatio.PORTRAIT:
      if (exactFitSideVideo === "width") {
        cropWidth = originalWidth;
        cropHeight = originalWidth * 1.5;
      } else {
        cropHeight = originalHeight;
        cropWidth = originalHeight / 1.5;
      }
      break;
  }

  // Round the cropping numbers in case the 1.5 factor created a decimal
  cropWidth = Math.round(cropWidth);
  cropHeight = Math.round(cropHeight);

  // Ensure the numbers are within bounds of the original dimensions
  let cropEndX = originalLeft + cropWidth;
  let cropEndY = originalTop + cropHeight;
  if (cropEndX > originalWidth) {
    cropWidth -= cropEndX - originalWidth;
  }
  if (cropEndY > originalHeight) {
    cropHeight -= cropEndY - originalHeight;
  }

  // Ensure the numbers are even
  cropWidth = cropWidth % 2 === 0 ? cropWidth : cropWidth - 1;
  cropHeight = cropHeight % 2 === 0 ? cropHeight : cropHeight - 1;

  // For square aspect ratio match the crop width and height if slightly off
  if (selectedAspectRatio === AspectRatio.SQUARE) {
    cropWidth = cropHeight = Math.min(cropWidth, cropHeight);
  }

  // Display the cropping info based on the calculated values
  /*
  displayCroppingInfo(
    originalTop,
    originalLeft,
    originalWidth,
    originalHeight,
    cropWidth,
    cropHeight,
    "Video"
  );
  */

  // Store cropping data in global videoCroppingData object
  videoCroppingData.videoUploadFile = videoUploadFile;
  videoCroppingData.originalWidth = originalWidth;
  videoCroppingData.originalHeight = originalHeight;
  videoCroppingData.originalLeft = originalLeft;
  videoCroppingData.originalTop = originalTop;
  videoCroppingData.cropWidth = cropWidth;
  videoCroppingData.cropHeight = cropHeight;
}

// Function to display cropping information
function displayCroppingInfo(
  originalTop,
  originalLeft,
  originalWidth,
  originalHeight,
  cropWidth,
  cropHeight,
  mediaType
) {
  // Calculate the crop to values
  const cropToRight = originalLeft + cropWidth;
  const cropToBottom = originalTop + cropHeight;

  // Calculate total pixels to crop off and the amount of pixels to transpose
  const totalHorizontalPixelsToCrop = originalWidth - cropWidth;
  const totalVerticalPixelsToCrop = originalHeight - cropHeight;
  const horizontalPixelsTransposed = originalLeft;
  const verticalPixelsTransposed = originalTop;

  // Display the information
  const info = `
        <h3>${mediaType} Cropping Information:</h3>
            <ul style="font-size: 1.2em; list-style-type: disc; padding-left: 20px; line-height: 1.5;">
            <li>Final Resolution: ${cropWidth} x ${cropHeight}</li>
            <li>Crop From: (${originalLeft}, ${originalTop})</li>
            <li>Crop To: (${cropToRight}, ${cropToBottom})</li>
            <li>Total Horizontal Pixels to Crop: ${totalHorizontalPixelsToCrop}</li>
            <li>Total Vertical Pixels to Crop: ${totalVerticalPixelsToCrop}</li>
            <li>Horizontal Pixels Transposed: ${horizontalPixelsTransposed}</li>
            <li>Vertical Pixels Transposed: ${verticalPixelsTransposed}</li>
            </ul>
    `;

  // Create the FFmpeg command for the video crop
  const ffmpegCommand = `
        <h3>FFmpeg Command:</h3>
        <ul style="font-size: 1.2em; list-style-type: disc; padding-left: 20px; line-height: 1.5;">
        <li>ffmpeg -i input.mp4 -vf "crop=${cropWidth}:${cropHeight}:${originalLeft}:${originalTop}" output.mp4</li>
        </ul>
    `;

  // Append this information to the appropriate output HTML element
  if (mediaType === "Image") {
    document.getElementById("image-output").innerHTML = info;
  } else if (mediaType === "Video") {
    document.getElementById("video-output").innerHTML = info;
    document.getElementById("ffmpeg-output").innerHTML = ffmpegCommand;
  }
}

// Function to show popup of custcom canvas order for customer to confirm before adding to cart
async function showCustomCanvasConfirmationPopup() {

  // Make sure Add to Cart and Make Changes buttons are enabled 
  const addToCartButton = document.getElementById("add-to-cart-button");
  const makeChangesButton = document.getElementById("make-changes-button");  
  const buttonEnabledColor_addToCart = "#6A31E3";
  const buttonEnabledColor_makeChanges = "#E0E0E0";
  addToCartButton.disabled = false;
  makeChangesButton.disabled = false;
  addToCartButton.style.backgroundColor = buttonEnabledColor_addToCart;
  makeChangesButton.style.backgroundColor = buttonEnabledColor_makeChanges; 
  makeChangesButton.style.opacity = 1.0;  

  // Get the Canvas Experience Name input (Default name added for error checking but this shouldn't happen
  // because the input shouldn't let user continue with a blank eperience name)
  let canvasExperienceName = document
    .getElementById("canvas-name-input")
    .value.trim();
  if (canvasExperienceName === "") {
    canvasExperienceName = "AR Teez Experience";
  }

  // Assign value for whether or not image passed the OpenCV image target evaluation test -- If value
  // is false, it is probably because OpeCV never loaded
  let imageTargetEvaluationPassValue;
  if (didImageTargetEvaluationPass) {
    imageTargetEvaluationPassValue = "Passed OpenCV features test";
  } else {
    imageTargetEvaluationPassValue = "OpenCV never loaded/Didn't pass";
  }

  // Update order snapshot to contain all order data
  orderSnapshot = {
    frameDepth: selectedFrameDepth,
    canvasSize: selectedCanvasSize,
    aspectRatio: selectedAspectRatio,
    imageUploadFile: imageUploadFile,
    videoUploadFile: videoUploadFile,
    imageCroppingData: imageCroppingData,
    videoCroppingData: videoCroppingData,
    canvasExperienceName: canvasExperienceName,
    canvasPrice: getCanvasPrice(),
    canvasQuantity: 1,
    imageTargetEvaluationPass: imageTargetEvaluationPassValue,
  };

  // Note: We start quantity with a default value of 1, but the actual quantity gets updated in the Cloud Function that is called
  // by the Shopify Payment Confirmation webhook, and we update the Job Tracking document in that Cloud Function
  
  // Create the string representation of the canvas size
  const { canvasWidth, canvasHeight } = getCanvasDimensions(orderSnapshot.aspectRatio, orderSnapshot.canvasSize);
  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };
  const canvasSizeString = `${canvasWidth}" x ${canvasHeight}" - ${capitalizeFirstLetter(orderSnapshot.canvasSize)}`;

  // Create the string representation of the frame depth
  const formatFrameDepth = (depth) => {
    if (depth === FrameDepth.INCH150) {
      return '1.5"';
    } else if (depth === FrameDepth.INCH75) {
      return '.75"';
    }
    return ''; // Fallback in case depth doesn't match expected values
  };
  const frameDepthString = formatFrameDepth(orderSnapshot.frameDepth);
  
  // Create the string representation of the canvas price
  const canvasPriceString = `$${orderSnapshot.canvasPrice}`;

  // Update popup with selected canvas information  
  document.querySelector('.confirmation-popup-info-value[name="canvas-name"]').innerText = canvasExperienceName;
  document.querySelector('.confirmation-popup-info-value[name="canvas-size"]').innerText = canvasSizeString;
  document.querySelector('.confirmation-popup-info-value[name="frame-depth"]').innerText = frameDepthString;
  document.querySelector('.confirmation-popup-info-value[name="canvas-price"]').innerText = canvasPriceString;

  // Load and set the canvas thumbnail image 
  const canvasPreviewThumbnail = document.getElementById("canvas-preview-thumbnail");
  try {
    const image = await getVisiblePortionOfImage();  // Wait for the image to be loaded
    canvasPreviewThumbnail.src = image.src;
    canvasPreviewThumbnail.alt = "Preview of the canvas";

    // Wait until thumbnail image is loaded before proceeding with QR code scaling and positioninf
    canvasPreviewThumbnail.onload = async function () {

      // Once the thumbnail image is fully loaded, scale and position the QR code
      await scaleAndPositionQrCodeForConfirmationPopupThumbnail();

      // Now create the mockup image for the cart
      await createCustomCanvasMockUpImageForCart();    
    }
    
  } catch (error) {
    console.error("Error loading canvas preview thumbnail:", error.message);
  }

  // Make confirmation popup visible and disable background scrolling
  document.getElementById('custom-canvas-confirmation-popup').style.opacity = 1;
  document.getElementById('custom-canvas-confirmation-popup').classList.remove('hidden'); 
  document.body.classList.add('no-scroll');
}

// Function to scale and position the QR code on confirmation popup so it aligns correctly with the canvas image thumbnail
async function scaleAndPositionQrCodeForConfirmationPopupThumbnail() {
  const thumbnailImage = document.getElementById('canvas-preview-thumbnail');
  const qrCodeOverlay = document.getElementById('qr-code-thumbnail-overlay');
  const thumbnailContainer = document.querySelector('.confirmation-popup-thumbnail-container');

  const imageWidth = thumbnailImage.clientWidth;
  const imageHeight = thumbnailImage.clientHeight;
  const containerWidth = thumbnailContainer.clientWidth;
  const containerHeight = thumbnailContainer.clientHeight;

  // Real-life dimensions of the QR code (in inches)
  const qrCodeWidthInInches = 2;

  // Get the canvas dimensions from another function (already calculated)
  const { canvasWidth, canvasHeight } = getCanvasDimensions(selectedAspectRatio, selectedCanvasSize);

  // Calculate QR code width in pixels based on canvas size
  const qrCodeWidthInPixels = (qrCodeWidthInInches / canvasWidth) * imageWidth;

  // Calculate the aspect ratio of the QR code (natural aspect ratio of the image)
  const qrCodeOriginalWidth = qrCodeOverlay.naturalWidth;
  const qrCodeOriginalHeight = qrCodeOverlay.naturalHeight;
  const qrCodeAspectRatio = qrCodeOriginalHeight / qrCodeOriginalWidth;

  // Calculate the height of the QR code based on its aspect ratio
  const qrCodeHeightInPixels = qrCodeWidthInPixels * qrCodeAspectRatio;

  // Calculate margin in pixels (half-inch away from the bottom-right of the thumbnail image)
  const qrCodeMarginInInches = 0.5;
  const qrCodeMarginBottomInPixels = (qrCodeMarginInInches / canvasHeight) * imageHeight;
  const qrCodeMarginRightInPixels = (qrCodeMarginInInches / canvasWidth) * imageWidth;

  // Center the thumbnail in the container
  const offsetX = (containerWidth - imageWidth) / 2;
  const offsetY = (containerHeight - imageHeight) / 2;

  // Position the QR code relative to the bottom-right corner of the thumbnail
  const qrCodeX = offsetX + imageWidth - qrCodeWidthInPixels - qrCodeMarginRightInPixels;
  const qrCodeY = offsetY + imageHeight - qrCodeHeightInPixels - qrCodeMarginBottomInPixels;

  // Apply the calculated position and size to the QR code overlay
  qrCodeOverlay.style.width = `${qrCodeWidthInPixels}px`;
  qrCodeOverlay.style.height = `${qrCodeHeightInPixels}px`;
  qrCodeOverlay.style.left = `${qrCodeX}px`;
  qrCodeOverlay.style.top = `${qrCodeY}px`;  
}

// Creates a mockup of the custom canvas that can be used as the thumbnail for the line item in the cart -- Image is added
// to the orderSnapshot object. When 'Add to Cart' button is pressed later, it can then be uploaded to Cloud Storage
// with the job ID created at that time. The Cloud Storage URL can then be used for the cart line item image
async function createCustomCanvasMockUpImageForCart() {

  // Get mockup background image paths for Cloud Storage
  const MOCKUP_BACKGROUND_SQUARE_IMAGE_PATH = 'custom-canvas-mockup-for-cart-template-images/canvas-mockup-for-cart-square-1155x1155.png';
  const MOCKUP_BACKGROUND_PORTRAIT_IMAGE_PATH = 'custom-canvas-mockup-for-cart-template-images/canvas-mockup-for-cart-portrait-1155x1155.png';
  const MOCKUP_BACKGROUND_LANDSCAPE_IMAGE_PATH = 'custom-canvas-mockup-for-cart-template-images/canvas-mockup-for-cart-landscape-1155x1155 .png';

  // Use selected aspect ratio to determine which background image path to assign and which thumbnail resize and position values to use
  let backgroundImagePath;
  let thumbnailImageResizedWidth;
  let thumbnailImageResizedHeight;
  let thumbnailImageX;
  let thumbnailImageY;
  if (selectedAspectRatio === AspectRatio.SQUARE) {
    backgroundImagePath = MOCKUP_BACKGROUND_SQUARE_IMAGE_PATH;
    thumbnailImageResizedWidth = 454;
    thumbnailImageResizedHeight = 454;
    thumbnailImageX = 47;
    thumbnailImageY = 23;
  } else if (selectedAspectRatio === AspectRatio.PORTRAIT) {
    backgroundImagePath = MOCKUP_BACKGROUND_PORTRAIT_IMAGE_PATH;
    thumbnailImageResizedWidth = 320;
    thumbnailImageResizedHeight = 480;
    thumbnailImageX = 114;
    thumbnailImageY = 10;
  } else {
    backgroundImagePath = MOCKUP_BACKGROUND_LANDSCAPE_IMAGE_PATH;
    thumbnailImageResizedWidth = 456;
    thumbnailImageResizedHeight = 304;
    thumbnailImageX = 40;
    thumbnailImageY = 105;
  }
  
  // Get the thumbnail and QR code elements
  const thumbnailImage = document.getElementById('canvas-preview-thumbnail');
  const qrCodeOverlay = document.getElementById('qr-code-thumbnail-overlay'); 

  // Draw the thumbnail to a canvas
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const canvasWidth = thumbnailImage.naturalWidth;
  const canvasHeight = thumbnailImage.naturalHeight;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  // Draw thumbnail image to canvas using inherent values
  context.drawImage(thumbnailImage, 0, 0, canvasWidth, canvasHeight);

  // Scale and position the QR code
  const qrCodeWidthInInches = 2;
  const qrCodeMarginInInches = 0.5;

  // Real-life dimensions of the canvas (in inches)
  const { canvasWidth: realLifeCanvasWidthInInches, canvasHeight: realLifeCanvasHeightInInches } = getCanvasDimensions(orderSnapshot.aspectRatio, orderSnapshot.canvasSize);
  
   // Calculate how many pixels per inch for the canvas
  const pixelsPerInchX = canvasWidth / realLifeCanvasWidthInInches;  // Pixels per inch in the X direction
  const pixelsPerInchY = canvasHeight / realLifeCanvasHeightInInches; // Pixels per inch in the Y direction

  // Calculate the aspect ratio of the QR code (natural aspect ratio of the image)
  const qrCodeOriginalWidth = qrCodeOverlay.naturalWidth;
  const qrCodeOriginalHeight = qrCodeOverlay.naturalHeight;
  const qrCodeAspectRatio = qrCodeOriginalHeight / qrCodeOriginalWidth;

  // Calculate the QR code width and height in pixels
  const qrCodeWidthInPixels = qrCodeWidthInInches * pixelsPerInchX;
  const qrCodeHeightInPixels = qrCodeWidthInPixels * qrCodeAspectRatio;

  // Calculate the margin in pixels from the bottom-right corner
  const qrCodeMarginBottomInPixels = qrCodeMarginInInches * pixelsPerInchY;
  const qrCodeMarginRightInPixels = qrCodeMarginInInches * pixelsPerInchX;

  // Position the QR code relative to the bottom-right corner of the canvas
  const qrCodeX = canvasWidth - qrCodeWidthInPixels - qrCodeMarginRightInPixels;
  const qrCodeY = canvasHeight - qrCodeHeightInPixels - qrCodeMarginBottomInPixels;

  // Draw the QR code on the canvas at the calculated position
  context.drawImage(qrCodeOverlay, qrCodeX, qrCodeY, qrCodeWidthInPixels, qrCodeHeightInPixels);  

  // Download the background image from Cloud Storage
  const storage = getStorage();
  const backgroundImageRef = ref(storage, backgroundImagePath);  

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';  // Allow cross-origin access for images served via URLs
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
    });
  }

  try {
    // Fetch the secure download URL for the background image
    const backgroundImageUrl = await getDownloadURL(backgroundImageRef);

    // Load the background image using the secure URL
    const backgroundImage = await loadImage(backgroundImageUrl);

    // Create a new canvas sized at 512 x 512 for the final composition
    const finalCanvas = document.createElement('canvas');
    const finalContext = finalCanvas.getContext('2d');

    const finalCanvasWidth = 512;
    const finalCanvasHeight = 512;
    finalCanvas.width = finalCanvasWidth;
    finalCanvas.height = finalCanvasHeight;

    // Draw the background image on the final canvas
    finalContext.drawImage(backgroundImage, 0, 0, finalCanvasWidth, finalCanvasHeight);

    // Draw the thumbnail/QR code composite onto the background canvas using the calculates resize and position values 
    // for the thumbnail based on the aspect ratio of the image
    finalContext.drawImage(canvas, thumbnailImageX, thumbnailImageY, thumbnailImageResizedWidth, thumbnailImageResizedHeight);

    // Convert the final composite canvas to a blob for uploading
    const finalCompositeImageBlob = await new Promise(resolve => finalCanvas.toBlob(resolve, 'image/png'));

    // Add to orderSnapshot object to be uploaded to Cloud Storage after 'Add to Cart' button is pressed, which is when we 
    // get the order ID -- After adding to Cloud Storage, we can use URL to display mockup thumbnail in line item of cart
    orderSnapshot.mockupThumbnail = finalCompositeImageBlob;    
    
    /*
    // Below is used for testing purposes -- In production, we add the composite image to the orderSnapshot object and wait 
    // until 'Add to Cart' button is pressed and we get actual job ID before uploading to Cloud Storage
    // Test job ID used for testing
    orderSnapshot.jobId = "test";    

    // Upload the final composite image as the mockup image for cart to Cloud Storage
    const mockupImageForCartCloudStoragePath = `custom-canvas-mockup-images-for-cart/${orderSnapshot.jobId}_mockupImageForCart.png`;

    // Assign QR code image metadata
    const customMetadata = {
      contentType: "image/png",
      customMetadata: {
        jobId: orderSnapshot.jobId,
      },
    };

    const mockupImageForCartUploadTaskSnapshot = await uploadBytes(
      ref(storage, mockupImageForCartCloudStoragePath),
      finalCompositeImageBlob,
      customMetadata
    );
    console.log("Composited image uploaded successfully:");
    */    
  
  } catch (error) {
    console.error("Error uploading composited image:", error);
  }  
}



// Function to go from the Custom Canvas Confirmation popup to beginning the processing of the order
// and adding it to the cart -- This includes creating the job tracking document, uploading the QR code, 
// the image, and the video to Cloud Storage, and confirming that all assets have uploaded successfully 
// so that order processing can take place before proceeding to add the custom canvas order to the cart 
// for payment
async function handleAddToCart() {

  // Disable both Add to Cart & Make Changes buttons while Firebase Auth check and add to cart
  // operations are processing to prevent multiple order submissions from ocucrring
  const addToCartButton = document.getElementById("add-to-cart-button");
  const makeChangesButton = document.getElementById("make-changes-button");  
  const buttonEnabledColor_addToCart = addToCartButton.style.backgroundColor;
  const buttonEnabledColor_makeChanges = makeChangesButton.style.backgroundColor;
  const buttonDisabledColor_addToCart = "#8A5FC9";
  const buttonDisabledColor_makeChanges = "#D3D3D3";
  addToCartButton.disabled = true;
  makeChangesButton.disabled = true;
  addToCartButton.style.backgroundColor = buttonDisabledColor_addToCart;
  makeChangesButton.style.backgroundColor = buttonDisabledColor_makeChanges; 
  makeChangesButton.style.opacity = 0.6;  

  // Display the progress overlay
  const progressOverlay = document.getElementById('progressOverlay');
  progressOverlay.classList.remove('hidden'); 
  progressOverlay.style.opacity = '1';
  progressOverlay.style.pointerEvents = 'auto';
  
  // Check that Firebase initialization is active and user is authenticated, and if not, refresh the token
  try {
    // Get current auth instance and user
    const firebaseAuth = getAuth();
    const firebaseUser = firebaseAuth.currentUser;

    // Check if user is authenticated
    if (firebaseUser) {

      // First check if token was refreshed over an hour ago, and if so, refresh it
      const tokenResult = await firebaseUser.getIdTokenResult();
      const authTime = new Date(tokenResult.authTime);
      const currentTime = new Date();
      const tokenExpiryThreshold = 60 * 60 * 1000;
      if (currentTime - authTime > tokenExpiryThreshold) {
        console.log("Firebase token is older than 60 minutes. Refreshing token");
        await firebaseUser.getIdToken(true);
      } else {
        console.log("Firebase user is authenticated and token is current");
      } 
      
    } else {
      // User is not authenticated, re-authenticating
      console.log("User is not authenticated...Attempting to re-authenticate");
      try {
        await signInAnonymously(firebaseAuth);
        console.log("Re-authenticated Firebase and signed in as an anonymous user");
        
      } catch (authError) {
        console.error("Anonymous re-authentication failed: ", authError.message);

        // Create alert for user        
        alert(`Oops! It seems there's an issue with your current browser session connecting to our servers.\n\nTo fix this, you may need to close and reopen your browser, as this can help resolve any session-related problems.`);

        // Hide the progress overlay
        const progressOverlay = document.getElementById('progressOverlay');
        progressOverlay.style.opacity = 0;
                
        // Re-enable Add to Cart button before returning
        addToCartButton.style.backgroundColor = buttonEnabledColor_addToCart;
        addToCartButton.disabled = false;
        makeChangesButton.style.backgroundColor = buttonEnabledColor_makeChanges;
        makeChangesButton.style.opacity = 1.0;
        makeChangesButton.disabled = false;
        return;
      }
    }
  } catch (error) {
    console.error("Other error during Firebase authentication check: ", error);

    // Create alert for user
    alert(`Oops! It seems there's an issue with your current browser session connecting to our servers.\n\nTo fix this, you may need to close and reopen your browser, as this can help resolve any session-related problems.`);

    // Hide the progress overlay
    const progressOverlay = document.getElementById('progressOverlay');
    progressOverlay.style.opacity = 0; 

    // Re-enable Add to Cart button if there's an authentication error
    addToCartButton.style.backgroundColor = buttonEnabledColor_addToCart;
    addToCartButton.disabled = false;
    makeChangesButton.style.backgroundColor = buttonEnabledColor_makeChanges;
    makeChangesButton.style.opacity = 1.0;
    makeChangesButton.disabled = false;
    return;
  }

  // Firebase auth was successful, proceed with uploading media to Cloud Storage, creating
  // Firestore tracking document, and remaining add to cart operations

  try {

    // Generate the permanent AR package code
    const arPackageCode = generateArPackageCode();

    // Create the variation-specific product code from the AR package code
    const productCode = generateProductCode(arPackageCode, selectedAspectRatio, selectedCanvasSize, selectedFrameDepth);

    // Update order snapshot with identifier codes and placeholders for order and job IDs
    orderSnapshot.arPackageCode = arPackageCode;
    orderSnapshot.productCode = productCode;
    orderSnapshot.orderId = "Waiting for order ID";
    orderSnapshot.jobId = "Waiting for job ID";

    // Check Firebase app initialization
    if (window.firebaseApp) {
      console.log("Firebase App is initialized:", window.firebaseApp);
    } else {
      console.error("Firebase App is not initialized.");
    }

    // Get Firebase image paths for QR code image templates
    const logoImageFirebasePath = "qr-code-template-images/AR-Teez-Logo-BlueBackground-DropShadow-672x672 (CMYK).png";
    const qrCodeBackgroundImageFirebasePath = "qr-code-template-images/QRCode-Template-BlueBackground-600x694 (CMYK-Safe).png";
    const qrCodeFrameImageFirebasePath = "qr-code-template-images/QRCode-Template-Frame-600x694 (CMYK-Safe).png";
    const storage = getStorage();

    // Get the URLs for the QR code images
    const [qrCodeLogoUrl, qrCodeBackgroundUrl, qrCodeFrameUrl] = await Promise.all([
      getDownloadURL(ref(storage, logoImageFirebasePath)),
      getDownloadURL(ref(storage, qrCodeBackgroundImageFirebasePath)),
      getDownloadURL(ref(storage, qrCodeFrameImageFirebasePath)),
    ]);

    // Generate QR code as a Blob and add it to ordersnapshot for uploading to Cloud Storage
    const qrCodeImageBlob = await generateQRCode(
      orderSnapshot.arPackageCode,
      qrCodeLogoUrl,
      qrCodeBackgroundUrl,
      qrCodeFrameUrl
    );
    orderSnapshot.qrCodeImageBlob = qrCodeImageBlob;

    // Proceed with uploading assets to Cloud Storage 
    await uploadAssetsToCloudStorage(orderSnapshot);

  } catch (error) {
    console.error("Error during order processing and media upload: ", error);
    // Handle any error during order processing or media upload

    // Re-enable Add to Cart and Make Changes buttons on erros
    addToCartButton.style.backgroundColor = buttonEnabledColor_addToCart;
    addToCartButton.disabled = false;
    makeChangesButton.style.backgroundColor = buttonEnabledColor_makeChanges;
    makeChangesButton.style.opacity = 1.0;
    makeChangesButton.disabled = false;  
    
  } finally {

    // Maybe delete this
    //
    // We moved reenabling the buttons to the error areas and to the beginning of the
    // Confirmation popup so that the buttons don't reenable in between the uploads 
    // completing and before the page moves to the cart
  }
}

// Creates the Firestore job tracking document for the order with the available information
// we have so far -- Job ID is created using Firestore's auto-id feature (20 characters) and
// prepending "JOB-" to it (Note: We will still not have the order ID available for this)
async function createJobTrackingDocument(orderSnapshot) {
  const firestoreDb = getFirestore();
  const jobTrackingCollection = FIRESTORE_JOB_TRACKING_COLLECTION;

  try {
    // Generate a temp document to get a unique Firestore auto-ID (does not incur cost) and prepend "JOB-"
    const tempDocRef = doc(collection(firestoreDb, "_tempCollection"));
    const firestoreAutoId = tempDocRef.id;
    const jobId = `JOB-${firestoreAutoId}`;

    // Update the orderSnapshot with the job ID
    orderSnapshot.jobId = jobId;

    const currentDate = Timestamp.fromDate(new Date());
    const jobStatus = {
      [FIELD_JOB_STATUS_MAP_IMAGE_STATUS]: "Uploading source file",
      [FIELD_JOB_STATUS_MAP_VIDEO_STATUS]: "Uploading source file",
      [FIELD_JOB_STATUS_MAP_QRCODE_STATUS]: "Uploading source file",
      [FIELD_JOB_STATUS_MAP_AR_PACKAGE_STATUS]: "Waiting",
      [FIELD_JOB_STATUS_MAP_CUSTOMCAT_STATUS]: "Waiting",
    };

    // Get the canvas width and height in inches to assign the canvas size in the job tracking document
    const { canvasWidth, canvasHeight } = getCanvasDimensions(
      orderSnapshot.aspectRatio,
      orderSnapshot.canvasSize
    );
    const canvasSizeForJobTrackingDoc = `${canvasWidth}x${canvasHeight}`;

    // Create the job tracking document using the custom job ID as the document name
    await setDoc(doc(firestoreDb, jobTrackingCollection, orderSnapshot.jobId), {
      [FIELD_JOB_ID]: orderSnapshot.jobId,
      [FIELD_ORDER_ID]: "Waiting for order ID",
      [FIELD_ORDER_PAYMENT_STATUS]: "Waiting for payment",
      [FIELD_PRODUCT_CODE]: orderSnapshot.productCode,
      [FIELD_AR_PACKAGE_CODE]: orderSnapshot.arPackageCode,
      [FIELD_CANVAS_EXPERIENCE_NAME]: orderSnapshot.canvasExperienceName,
      [FIELD_JOB_CREATION_DATE]: serverTimestamp(),
      [FIELD_QRCODE_SOURCE_UPLOAD_STATUS]: "Waiting",
      [FIELD_QRCODE_SOURCE_PATH]: "",
      [FIELD_IMAGE_SOURCE_UPLOAD_STATUS]: "Waiting",
      [FIELD_IMAGE_SOURCE_PATH]: "",
      [FIELD_IMAGE_TARGET_EVALUATION_PASS]: orderSnapshot.imageTargetEvaluationPass,
      [FIELD_VIDEO_SOURCE_UPLOAD_STATUS]: "Waiting",
      [FIELD_VIDEO_SOURCE_PATH]: "",
      [FIELD_JOB_JOB_STATUS]: jobStatus,
      [FIELD_CANVAS_DEPTH]: orderSnapshot.frameDepth,
      [FIELD_CANVAS_SHAPE]: orderSnapshot.aspectRatio,
      [FIELD_CANVAS_SIZE]: canvasSizeForJobTrackingDoc,
      [FIELD_CANVAS_PRICE]: orderSnapshot.canvasPrice,
      [FIELD_CANVAS_QUANTITY]: orderSnapshot.canvasQuantity,
      [FIELD_CUSTOMER_CUSTOMER_TIME_ORDER_DATE]: currentDate,
    });

    // Note: We start quantity with a default value of 1, but the actual quantity gets updated in the Cloud Function that is called
    // by the Shopify Payment Confirmation webhook, and we update the Job Tracking document in that Cloud Function

    console.log(`Firestore job tracking document ${jobId} created`);
    return jobId;
  } catch (error) {
    console.error("Error creating Firestore document: ", error);
    throw new Error("Failed to create Firestore job tracking document.");
  }  
}

// Function to set up the upload for all assets to Cloud Storage
// This is the initial function to upload assets, it will set up the metadta for each asset, 
// determine the largest file (for the progress function), and track the successful uploads 
// in uploadStatus. This function also determines (on initial attempt) whether all uploads
// were successful and, if so, will be responsible for moving the progress bar to 100%, and in
// determining whether we can procced to adding the order information to the cart or if there
// was an upload issue and we need to give the user upload error options to retry or cancel
async function uploadAssetsToCloudStorage(orderSnapshot) {

  // Create Firestore tracking document and get the job ID
  const jobId = await createJobTrackingDocument(orderSnapshot);

  // Get the document reference for the tracking document to update the tracking document
  const docRef = doc(getFirestore(), FIRESTORE_JOB_TRACKING_COLLECTION, orderSnapshot.jobId);

  // Define the initial upload status for each file -- Used to track succesful vs failed uploads for retries
  const uploadStatus = {
    qrCode: { status: 'pending', error: null },
    image: { status: 'pending', error: null },
    video: { status: 'pending', error: null },
    maxRetries: 3,
    retryCount: 0
  };

  // Determine the largest file for tracking progress (progress bar only tracks the largest upload)
  const fileSizes = {
    qrCode: orderSnapshot.qrCodeImageBlob.size,
    image: orderSnapshot.imageUploadFile.size,
    video: orderSnapshot.videoUploadFile.size
  };
  const largestFile = Object.keys(fileSizes).reduce((a, b) => fileSizes[a] > fileSizes[b] ? a : b);

  // Assign the QR code metadata
  const qrCodeMetadata = {
    contentType: "image/png",
    customMetadata: {
      jobId: orderSnapshot.jobId,
      orderId: orderSnapshot.orderId,
      productCode: orderSnapshot.productCode,
      arPackageCode: orderSnapshot.arPackageCode,
      // [FIELD_MEDIA_PROCESSING_STATUS]: MediaProcessingStatus.WAITING_FOR_PAYMENT, // Not needed for QR code
    },
  };

  // Setup image custom metadata
  // Set the resize values for the image based on the selected canvas size and aspect ratio in order snapshot
  // Multiply canvas inches dimensions by 300 for printing at 300 dpi -- Resize values correspond
  // to the size we will be upscaling the image to in order to match the selected canvas size
  const { canvasWidth, canvasHeight } = getCanvasDimensions(
    orderSnapshot.aspectRatio,
    orderSnapshot.canvasSize
  );
  const resizeWidth = canvasWidth * 300;
  const resizeHeight = canvasHeight * 300;

  // Append canvasSize from order snapshot with the actual inches to get a canvas size string for the metadata
  // Assign it to canvasSize in metadata
  const canvasSizeString = `${canvasWidth}x${canvasHeight}-${orderSnapshot.canvasSize}`;

  // Assign image metadata
  const imageMetadata = {
    customMetadata: {
      imageWidth: orderSnapshot.imageCroppingData.originalWidth,
      imageHeight: orderSnapshot.imageCroppingData.originalHeight,
      cropStartX: orderSnapshot.imageCroppingData.originalLeft,
      cropStartY: orderSnapshot.imageCroppingData.originalTop,
      cropWidth: orderSnapshot.imageCroppingData.cropWidth,
      cropHeight: orderSnapshot.imageCroppingData.cropHeight,
      resizeWidth: resizeWidth,
      resizeHeight: resizeHeight,
      canvasShape: orderSnapshot.aspectRatio,
      canvasSize: canvasSizeString,
      canvasDepth: orderSnapshot.frameDepth,
      arPackageCode: orderSnapshot.arPackageCode,
      productCode: orderSnapshot.productCode,
      orderId: orderSnapshot.orderId,
      jobId: orderSnapshot.jobId,
      uploadFilename: orderSnapshot.imageUploadFile.name,
      [FIELD_MEDIA_PROCESSING_STATUS]: MediaProcessingStatus.WAITING_FOR_PAYMENT,
    },
  };

  // Assign video metadata
  const videoMetadata = {
    customMetadata: {
      videoWidth: orderSnapshot.videoCroppingData.originalWidth,
      videoHeight: orderSnapshot.videoCroppingData.originalHeight,
      cropStartX: orderSnapshot.videoCroppingData.originalLeft,
      cropStartY: orderSnapshot.videoCroppingData.originalTop,
      cropWidth: orderSnapshot.videoCroppingData.cropWidth,
      cropHeight: orderSnapshot.videoCroppingData.cropHeight,
      arPackageCode: orderSnapshot.arPackageCode,
      productCode: orderSnapshot.productCode,
      orderId: orderSnapshot.orderId,
      jobId: orderSnapshot.jobId,
      uploadFilename: orderSnapshot.videoUploadFile.name,
      [FIELD_MEDIA_PROCESSING_STATUS]: MediaProcessingStatus.WAITING_FOR_PAYMENT,
    },
  };

  // Create Cloud Storage path for QR Code -- Name it [jobId]_qrCodeImage.png
  const qrCodeImageCloudStoragePath = `custom-canvas-qrcode-images/${orderSnapshot.jobId}_qrCodeImage.png`;

  // Create Cloud Storage path for image -- Name it [orderSnapshot.jobId]_sourceImage.png
  const imageCloudStoragePath = `custom-canvas-source-images/${orderSnapshot.jobId}_sourceImage.png`;

  // Create Cloud Storage path for video -- Name it [orderSnapshot.jobId]_sourceVideo.[fileExtension] (get video extension from orig file name)
  const fileExtension = orderSnapshot.videoUploadFile.name.split(".").pop(); 
  const videoCloudStoragePath = `custom-canvas-source-videos/${orderSnapshot.jobId}_sourceVideo.${fileExtension}`;

  // Start all three uploads asynchronously and store promises
  const qrCodeUploadPromise = uploadFileToCloudStorage(orderSnapshot.qrCodeImageBlob, qrCodeMetadata, qrCodeImageCloudStoragePath, largestFile === 'qrCode');
  const imageUploadPromise = uploadFileToCloudStorage(orderSnapshot.imageUploadFile, imageMetadata, imageCloudStoragePath, largestFile === 'image');
  const videoUploadPromise = uploadFileToCloudStorage(orderSnapshot.videoUploadFile, videoMetadata, videoCloudStoragePath, largestFile === 'video');

  // Update Firestore tracking documents on completion of each upload
  qrCodeUploadPromise
    .then((qrCodeImageCloudStoragePath) => {
      updateDoc(docRef, {
        [FIELD_QRCODE_SOURCE_UPLOAD_STATUS]: "Completed",
        [FIELD_QRCODE_SOURCE_PATH]: qrCodeImageCloudStoragePath,
        [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_QRCODE_STATUS}`]: "Completed",
      });
      uploadStatus.qrCode.status = 'completed';
    })
    .catch((error) => {
      console.error("Error uploading QR code: ", error);
      updateDoc(docRef, {
        [FIELD_QRCODE_SOURCE_UPLOAD_STATUS]: "Failed",
        [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_QRCODE_STATUS}`]: "Failed",
      });
      uploadStatus.qrCode = { status: 'failed', error};
    });      

  imageUploadPromise
    .then((imageCloudStoragePath) => {
      updateDoc(docRef, {
        [FIELD_IMAGE_SOURCE_UPLOAD_STATUS]: "Completed",
        [FIELD_IMAGE_SOURCE_PATH]: imageCloudStoragePath,
        [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_IMAGE_STATUS}`]: "Source uploaded to Cloud Storage",
      });
      uploadStatus.image.status = 'completed';      
    })
    .catch((error) => {
      console.error("Error uploading image: ", error);
      updateDoc(docRef, {
        [FIELD_IMAGE_SOURCE_UPLOAD_STATUS]: "Failed",
        [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_IMAGE_STATUS}`]: "Failed",
      });
      uploadStatus.image = { status: 'failed', error };
    });

  videoUploadPromise
    .then((videoCloudStoragePath) => {
      updateDoc(docRef, {
        [FIELD_VIDEO_SOURCE_UPLOAD_STATUS]: "Completed",
        [FIELD_VIDEO_SOURCE_PATH]: videoCloudStoragePath,
        [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_VIDEO_STATUS}`]: "Source uploaded to Cloud Storage",
      });
      uploadStatus.video.status = 'completed';
    })
    .catch((error) => {
      console.error("Error uploading video: ", error);
      updateDoc(docRef, {
        [FIELD_VIDEO_SOURCE_UPLOAD_STATUS]: "Failed",
        [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_VIDEO_STATUS}`]: "Failed",
      });
      uploadStatus.video = { status: 'failed', error };
    });

  // Await all three uploads to ensure they have all been processed 
  await Promise.allSettled([qrCodeUploadPromise, imageUploadPromise, videoUploadPromise]);

  // Check if there are any failed uploads and handle retry logic
  if (Object.values(uploadStatus).some(upload => upload.status === 'failed')) {
    // Create an uploadData object with all the variables needed to retry an upload each asset
    const uploadData = {
      qrCode: {
        file: orderSnapshot.qrCodeImageBlob,
        metadata: qrCodeMetadata,
        storagePath: qrCodeImageCloudStoragePath
      },
      image: {
        file: orderSnapshot.imageUploadFile,
        metadata: imageMetadata,
        storagePath: imageCloudStoragePath
      },
      video: {
        file: orderSnapshot.videoUploadFile,
        metadata: videoMetadata,
        storagePath: videoCloudStoragePath
      }
    };
    
    // Trigger retry overlay and pass the uploadStatus object and the uploadData object for further handling and retries
    handleUploadError(uploadStatus, uploadData);
  } else {
    // All uploads are successful, show upload complete status on progress bar
    updateProgressBar(100, 'Upload Complete!');
    
    // Proceed with adding order as a line item to the cart
    addOrderLineItemToCart();
  }
}

// Generic function to upload file with progress tracking -- shouldTrackProgress comes from which file of the ones
// currently being uploaded is the largest, because this is one that will be used for the visual progress tracking 
// bar displayed to the user
async function uploadFileToCloudStorage(file, customMetadata, storagePath, shouldTrackProgress) {
  const storage = getStorage();
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, file, customMetadata);
  
  let stageStartTime = Date.now();

  // Define the stages and their respective parameters
  // - maxTime is used for when we want a stage update to be displayed for a max amount of time before moving on to the next stage update 
  // - quickThreshold is an override to quickly move the stages with a proportional amount of time for super quick uploads 
  // - proportionalThreshold is the default progress indicator to move through the stages starting with the Processing Image stage 
  // and moving forward. These values are recalculated in real-time once we get to the Processing Image stage based on what percent 
  // of the upload is still left by that time
  const stages = {
    PREPARING_UPLOADS: { text: 'Preparing Uploads...', maxTime: 1000, quickThreshold: 14, proportionalThreshold: 10 },
    CREATING_QR_CODE: { text: 'Creating QR Code...', maxTime: 2000, quickThreshold: 29, proportionalThreshold: 20 },
    PROCESSING_IMAGE: { text: 'Processing Image...', maxTime: null, quickThreshold: 43, proportionalThreshold: 27},
    UPLOADING_IMAGE: { text: 'Uploading Image...', maxTime: null, quickThreshold: 57, proportionalThreshold: 55},
    PROCESSING_VIDEO: { text: 'Processing Video...', maxTime: null, quickThreshold: 71, proportionalThreshold: 62},
    UPLOADING_VIDEO: { text: 'Uploading Video...', maxTime: null, quickThreshold: 86, proportionalThreshold: 90},
    FINALIZING: { text: 'Finalizing...', maxTime: null, quickThreshold: 100, proportionalThreshold: 100},
    UPLOAD_COMPLETE: { text: 'Upload Complete!', maxTime: null, quickThreshold: null, proportionalThreshold: 100 },
  };

  let currentStage = stages.PREPARING_UPLOADS; // Start with the first stage
  let wereProportionalThresholdsCalculated = false;
  
  return new Promise((resolve, reject) => {

    /*
    // The following is only used for testing failed uploads -- It's mechanism to have the upload have a 75% chance
    // of failing -- Keep commented out when not testing
    let uploadCompleted = false;
    if (Math.random() < 0.75) {
      // 75% chance to cancel the upload after 1 second
      setTimeout(() => {
        if (!uploadCompleted) {
          uploadTask.cancel(); // Cancels the upload to simulate an error
        }
      }, 1000); // This will cancel the upload after 1 second
    }
    */   
    
    uploadTask.on('state_changed',
      (snapshot) => {
        // Progress updates are only calculated for the largest of the current uploads
        if (shouldTrackProgress) {
          let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          const elapsedTime = Date.now() - stageStartTime;

          if (currentStage === stages.PREPARING_UPLOADS) {
            // Preparing Uploads stage (1 second or 14%, whichever comes first)
            if (elapsedTime > currentStage.maxTime || progress > currentStage.quickThreshold) {
              currentStage = stages.CREATING_QR_CODE;
              stageStartTime = Date.now();
            }
            
          } else if (currentStage === stages.CREATING_QR_CODE) {
            //Creating QR Code stage (2 seconds or 29%, whichever comes first)
            if (elapsedTime > currentStage.maxTime || progress > currentStage.quickThreshold) {
              currentStage = stages.PROCESSING_IMAGE;
              stageStartTime = Date.now();
            }
            
          } else if (currentStage === stages.PROCESSING_IMAGE ) {
            if (!wereProportionalThresholdsCalculated) {
              // One time calculation for the remainging stage proportional thresholds based on how many bytes are left to be transferred
              // Both image stages should take up half of the bytes between what's remaining and 90% of total bytes, video stages take up the other half
              // Values converted to percentage of total bytes
              const allotmentToBothImageStages = ((((snapshot.totalBytes * .9) - (snapshot.bytesTransferred)) / 2) / snapshot.totalBytes) * 100;
              const processingStageAllotment = allotmentToBothImageStages * .2;
              const uploadingStageAllotment = allotmentToBothImageStages - processingStageAllotment;
              const currentPercentageTransferred = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

              // Use values above to create proportional thresholds for image and video stages (processing takes 20% of allotment, and uploading takes 80%)
              // Use Math min/max at 0 and 100 to ensure everything stays within valid percentage bounds
              stages.PROCESSING_IMAGE.proportionalThreshold = Math.min(100, Math.max(0, currentPercentageTransferred + processingStageAllotment));
              stages.UPLOADING_IMAGE.proportionalThreshold = Math.min(100, Math.max(0, stages.PROCESSING_IMAGE.proportionalThreshold + uploadingStageAllotment));
              stages.PROCESSING_VIDEO.proportionalThreshold = Math.min(100, Math.max(0, stages.UPLOADING_IMAGE.proportionalThreshold + processingStageAllotment));
              stages.UPLOADING_VIDEO.proportionalThreshold = Math.min(100, Math.max(0, stages.PROCESSING_VIDEO.proportionalThreshold + uploadingStageAllotment));
              wereProportionalThresholdsCalculated = true;

              // Edge case for when bytes transferred is already passed 90% or 100% -- Skip right to Finalizing stage or Upload Complete 
              if (progress >= 100) {
                currentStage = stages.UPLOAD_COMPLETE;
                stageStartTime = Date.now();
                
              } else if (progress > 90) {
                currentStage = stages.FINALIZING; 
                stageStartTime = Date.now();
              }           
            
            } else {
              // Processing Image stage (20% of the halfway point between bytes transferred after CREATING_QR_CODE stage and 90%)
              if (progress > currentStage.proportionalThreshold) {
                currentStage = stages.UPLOADING_IMAGE;
                stageStartTime = Date.now();  
              }

              // Edge case for when bytes transferred is already passed 90% or 100% -- Skip right to Finalizing stage or Upload Complete 
              if (progress >= 100) {
                currentStage = stages.UPLOAD_COMPLETE;
                stageStartTime = Date.now();
                
              } else if (progress > 90) {
                currentStage = stages.FINALIZING; 
                stageStartTime = Date.now();
              } 
            }

          // After we enter Uploading Image stage, do a reverse check on remaining stages progress thresholds so we can skip right to the
          // stage we should be at based on the current amount of bytes transferred and we don't get stuck in an earlier stage
          } else if (progress >= 100) {
            currentStage = stages.UPLOAD_COMPLETE;
            stageStartTime = Date.now();
          } else if (progress > stages.UPLOADING_VIDEO.proportionalThreshold) {
            currentStage = stages.FINALIZING;
            stageStartTime = Date.now();            
          } else if (progress > stages.PROCESSING_VIDEO.proportionalThreshold) {
            currentStage = stages.UPLOADING_VIDEO;
            stageStartTime = Date.now();
          } else if (progress > stages.UPLOADING_IMAGE.proportionalThreshold) {
            currentStage = stages.PROCESSING_VIDEO;
            stageStartTime = Date.now();
          }

          // Forcing progress and stage to stay at 99% and FINALIZING even when at 100% so we can use the calling method to confirm
          // all uploads completed (not just the largest one) before saying 100% and UPLOAD COMPLETE
          if (progress > 99) {
            currentStage = stages.FINALIZING;
            progress = 99;
          }

          // Update the progress bar
          updateProgressBar(progress, currentStage.text);
        }
      },
      (error) => {
        console.error(`Error uploading file to ${storagePath}: `, error);
        reject(new Error(`Failed to upload file to ${storagePath}.`));
      },
      () => {
        // uploadCompleted = true; // Only used for when testing failed uploads -- Otherwise, keep commented out
        resolve(storagePath);
      }
    );
  });  
}

// Function to update the progress bar and status text dynamically
function updateProgressBar(progress, statusText) {
  const progressBar = document.getElementById('progressBar');
  const progressText = document.getElementById('progressText');
  
  progressBar.style.width = `${progress}%`;
  progressBar.textContent = `${Math.round(progress)}%`;
  
  // Update the status text
  progressText.textContent = statusText;
}

// Function to handle when at least one of the uploads fails -- Updates the overlay to the error message
// for the user and provides retry and cancel option
function handleUploadError(uploadStatus, uploadData) {
  
  // Replace overlay message with error message
  const overlayMessage = document.getElementById('overlayMessage');
  const errorMessage = document.getElementById('errorMessage');
  const progressText = document.getElementById('progressText');
  const uploadErrorButtonContainer = document.getElementById('uploadErrorButtonContainer');

  // Hide current overlay message and progress text
  overlayMessage.style.display = 'none';
  progressText.style.display = 'none';

  // Show error message and error buttons
  errorMessage.style.display = 'block';
  uploadErrorButtonContainer.style.display = 'flex';  

  // Because the uploadStatus & uploadData variables change every time we come into the function, we must remove the 
  // previously assigned event listener and attach a new with the uploadStatus & uploadData values -- To do this 
  // properly, we have to 1) create new handlers with the current values each time, 2) Use the fact that we know the 
  // handler names to remove the old handlers from the buttons, and then 3) attach the newly defined handlers to the buttons

  // Define the handlers with the current values
  const retryHandler = () => retryFailedUploads(uploadStatus, uploadData);
  const cancelHandler = () => cancelFailedUploads(uploadStatus, uploadData);

  // Get the button objects
  const retryButton = document.getElementById('retryUploadButton');
  const cancelButton = document.getElementById('cancelUploadButton');

  // Remove the old handlers from the button
  retryButton.removeEventListener('click', retryButton.retryHandler);
  cancelButton.removeEventListener('click', cancelButton.cancelHandler);

  // Add the new listeners and attach the new handler references to the buttons to track them
  retryButton.retryHandler = retryHandler;
  cancelButton.cancelHandler = cancelHandler;  
  retryButton.addEventListener('click', retryHandler);
  cancelButton.addEventListener('click', cancelHandler);
}

// Function used to retry uploads if user decides -- This function is similar to the uploadAssetsToCloudStorage 
// function as it does things like initiate the uploads again, determine the current largest file for progress 
// tracking, tracking which uploads are successful, and being responsible for making the progress bar show 100% 
// one all uploads are successful -- The difference between the uploadAssetsToCloudStorage function and this is 
// this is retry method that remains on a loop until the uploads are successful or the user cancels
async function retryFailedUploads(uploadStatus, uploadData) {
  console.log("Retrying failed uploads...");

  // Only allow user to retry 3 times and then cancel and ask user to try again when
  // connection is more stable
  uploadStatus.retryCount++;

  // Hide error message and buttons  
  const errorMessage = document.getElementById('errorMessage');
  const uploadErrorButtonContainer = document.getElementById('uploadErrorButtonContainer');
  errorMessage.style.display = 'none';
  uploadErrorButtonContainer.style.display = 'none';  
  
  // Show the normal overlay message and progress text
  const overlayMessage = document.getElementById('overlayMessage');
  const progressText = document.getElementById('progressText');
  overlayMessage.style.display = 'block';
  progressText.style.display = 'block'; // Ensure the progress text is visible
  
  // Get the document reference for the tracking document to update the tracking document
  const docRef = doc(getFirestore(), FIRESTORE_JOB_TRACKING_COLLECTION, orderSnapshot.jobId);

  // Determine which files failed
  const failedFiles = Object.keys(uploadStatus).filter(key => uploadStatus[key].status === 'failed');

  // Re-evaluate the largest file among the failed ones
  const failedFileSizes = failedFiles.reduce((obj, key) => {
    obj[key] = uploadData[key].file.size;
    return obj;
  }, {});
  const largestFailedFile = Object.keys(failedFileSizes).reduce((a, b) => failedFileSizes[a] > failedFileSizes[b] ? a : b);

  // Keep track of retry promises
  const retryPromises = [];

  // Check each upload status and retry only the failed ones
  if (uploadStatus.qrCode.status === 'failed') {
    const qrCodeUploadPromise = uploadFileToCloudStorage(uploadData.qrCode.file, uploadData.qrCode.metadata, uploadData.qrCode.storagePath, largestFailedFile === 'qrCode')
      .then((path) => {
        uploadStatus.qrCode.status = 'completed';
        updateDoc(docRef, {
          [FIELD_QRCODE_SOURCE_UPLOAD_STATUS]: "Completed",
          [FIELD_QRCODE_SOURCE_PATH]: path,
          [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_QRCODE_STATUS}`]: "Completed",
        });
      }).catch((error) => {
        uploadStatus.qrCode.status = 'failed';
        uploadStatus.qrCode.error = error;
        updateDoc(docRef, {
          [FIELD_QRCODE_SOURCE_UPLOAD_STATUS]: "Failed",
          [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_QRCODE_STATUS}`]: "Failed",
        });
      });
    retryPromises.push(qrCodeUploadPromise);
  }

  if (uploadStatus.image.status === 'failed') {
    const imageUploadPromise = uploadFileToCloudStorage(uploadData.image.file, uploadData.image.metadata, uploadData.image.storagePath, largestFailedFile === 'image')
      .then((path) => {
        uploadStatus.image.status = 'completed';
        updateDoc(docRef, {
          [FIELD_IMAGE_SOURCE_UPLOAD_STATUS]: "Completed",
          [FIELD_IMAGE_SOURCE_PATH]: path,
          [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_IMAGE_STATUS}`]: "Source uploaded to Cloud Storage",
        });
      }).catch((error) => {
        uploadStatus.image.status = 'failed';
        uploadStatus.image.error = error;
        updateDoc(docRef, {
          [FIELD_IMAGE_SOURCE_UPLOAD_STATUS]: "Failed",
          [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_IMAGE_STATUS}`]: "Failed",
        });
      });
    retryPromises.push(imageUploadPromise);
  }

  if (uploadStatus.video.status === 'failed') {
    const videoUploadPromise = uploadFileToCloudStorage(uploadData.video.file, uploadData.video.metadata, uploadData.video.storagePath, largestFailedFile === 'video')
      .then((path) => {
        uploadStatus.video.status = 'completed';
        updateDoc(docRef, {
          [FIELD_VIDEO_SOURCE_UPLOAD_STATUS]: "Completed",
          [FIELD_VIDEO_SOURCE_PATH]: path,
          [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_VIDEO_STATUS}`]: "Source uploaded to Cloud Storage",
        });
      }).catch((error) => {
        uploadStatus.video.status = 'failed';
        uploadStatus.video.error = error;
        updateDoc(docRef, {
          [FIELD_VIDEO_SOURCE_UPLOAD_STATUS]: "Failed",
          [`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_VIDEO_STATUS}`]: "Failed",
        });
      });
    retryPromises.push(videoUploadPromise);
  }

  // Await all retries to complete whether they fail or not so we can evaluate uploadStatus next and determine
  // if we need to call handleUploadError again or proceed with adding the custom order to the cart
  await Promise.allSettled(retryPromises);

  // Handle further success or failure
  if (Object.values(uploadStatus).some(upload => upload.status === 'failed')) {

    // Only allow user to retry 3 times and then cancel and ask user to try again when
    // connection is more stable
    if (uploadStatus.retryCount < uploadStatus.maxRetries) {
      handleUploadError(uploadStatus, uploadData);
    } else {
      forcedCancelFailedUploads(uploadStatus, uploadData);
    }
  } else {
    // All uploads are successful, show upload complete status on progress bar
    updateProgressBar(100, 'Upload Complete!');

    // Proceed with adding order as a line item to the cart
    addOrderLineItemToCart();
  }
}

// Function for the user to cancel the failed uploads -- This will attempt to delete all the asset uploads that were 
// successful, update the job tracking document to a User Cancelled status, and return to the design canvas screen
// If the user decides to re-confirm the order and upload again, it will be assigned a new job tracking number and 
// document at that point -- Note: On a fail upload if the user closes the browser without pressing the 'Cancel for Now'
// button then this function does not run and the assets from the cancelled order are not deleted from Cloud Storage
async function cancelFailedUploads(uploadStatus, uploadData) {
  // Get the document reference for the tracking document to update the tracking document
  const docRef = doc(getFirestore(), FIRESTORE_JOB_TRACKING_COLLECTION, orderSnapshot.jobId);
  
  try {
    // Delete the uploads in Cloud Storage related to this job, whether they were successful or not to be thorough
    const deletePromises = [];
    const qrCodeRef = ref(getStorage(), uploadData.qrCode.storagePath);
    const imageRef = ref(getStorage(),uploadData.image.storagePath);
    const videoRef = ref(getStorage(), uploadData.video.storagePath);
    deletePromises.push(deleteObject(qrCodeRef));
    deletePromises.push(deleteObject(imageRef));
    deletePromises.push(deleteObject(videoRef));

    // Wait for all delete promises to complete (not necessary but useful for debugging)
    // await Promise.allSettled(deletePromises);
    // console.log("All assets deleted");

    // Mark the upload statuses for the successful uploads as Deleted in the job tracking document
    const jobTrackingUpdates = {};
    if (uploadStatus.qrCode.status === 'completed') {
      jobTrackingUpdates[FIELD_QRCODE_SOURCE_UPLOAD_STATUS] = 'Deleted - User Cancelled after upload failure';
      jobTrackingUpdates[`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_QRCODE_STATUS}`] = 'Deleted - User Cancelled after upload failure';
    }
    if (uploadStatus.image.status === 'completed') {
      jobTrackingUpdates[FIELD_IMAGE_SOURCE_UPLOAD_STATUS] = 'Deleted - User Cancelled after upload failure';
      jobTrackingUpdates[`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_IMAGE_STATUS}`] = 'Deleted - User Cancelled after upload failure';
    }
    if (uploadStatus.video.status === 'completed') {
      jobTrackingUpdates[FIELD_VIDEO_SOURCE_UPLOAD_STATUS] = 'Deleted - User Cancelled after upload failure';
      jobTrackingUpdates[`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_VIDEO_STATUS}`] = 'Deleted - User Cancelled after upload failure';
    }

    // Update the job tracking order id and payment status with User Cancelled after upload failure status
    jobTrackingUpdates[FIELD_ORDER_ID] = 'User Cancelled after upload failure';
    jobTrackingUpdates[FIELD_ORDER_PAYMENT_STATUS] = 'User Cancelled after upload failure';
    await updateDoc(docRef, jobTrackingUpdates);

    // Hide error message and buttons
    const errorMessage = document.getElementById('errorMessage');
    const uploadErrorButtonContainer = document.getElementById('uploadErrorButtonContainer');
    errorMessage.style.display = 'none';
    uploadErrorButtonContainer.style.display = 'none';

    // Switch the overlay messages back to the normal overlay message and progress text
    const overlayMessage = document.getElementById('overlayMessage');
    const progressText = document.getElementById('progressText');
    overlayMessage.style.display = 'block';
    progressText.style.display = 'block'; // Ensure the progress text is visible

    // Reset progress bar to 0% and to Preparing Uploads text
    updateProgressBar(0, "Preparing Uploads...");    

    // Hide the overlay and confirmation popup screens
    const progressOverlay = document.getElementById('progressOverlay');
    const confirmationPopup = document.getElementById('custom-canvas-confirmation-popup');
    progressOverlay.style.opacity = 0; // Fade out the overlay

    // Hide confirmation popup and re-enable scrolling on the page after a slight delay
    confirmationPopup.style.opacity = 0;
    setTimeout(function () {
      confirmationPopup.classList.add('hidden');
      document.body.classList.remove('no-scroll');
      progressOverlay.classList.add('hidden'); // Hide the progress overlay
    }, 300); // Matches the CSS fade-out time
    
  } catch (error) {
    console.error('Error trying to delete the uploads and the job tracking document on user cancel due to upload failure:', error);    
  }  
}

// Function for force the cancel after 3 failed uploads -- This will provide similar functionality as the user-initiated 
// cancel after failed uploads but this one is forced on the user after 3 failed retry attemps (cause is probably a poor 
// connection) -- This will attempt to delete all the asset uploads that were successful, update the job tracking document
// to a Forced Cancelled status, and return to the design canvas screen -- If the user decides to re-confirm the order and
// upload again, it will be assigned a new job tracking number and document at that point
async function forcedCancelFailedUploads(uploadStatus, uploadData) {
   // Get the document reference for the tracking document to update the tracking document
  const docRef = doc(getFirestore(), FIRESTORE_JOB_TRACKING_COLLECTION, orderSnapshot.jobId);
  
  try {
    // Delete the uploads in Cloud Storage related to this job, whether they were successful or not to be thorough
    const deletePromises = [];
    const qrCodeRef = ref(getStorage(), uploadData.qrCode.storagePath);
    const imageRef = ref(getStorage(),uploadData.image.storagePath);
    const videoRef = ref(getStorage(), uploadData.video.storagePath);
    deletePromises.push(deleteObject(qrCodeRef));
    deletePromises.push(deleteObject(imageRef));
    deletePromises.push(deleteObject(videoRef));

    // Wait for all delete promises to complete (not necessary but useful for debugging)
    // await Promise.allSettled(deletePromises);
    // console.log("All assets deleted");

    // Mark the upload statuses for the successful uploads as Deleted in the job tracking document
    const jobTrackingUpdates = {};
    if (uploadStatus.qrCode.status === 'completed') {
      jobTrackingUpdates[FIELD_QRCODE_SOURCE_UPLOAD_STATUS] = 'Deleted - Force Cancelled after 3 upload failures';
      jobTrackingUpdates[`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_QRCODE_STATUS}`] = 'Deleted - Force Cancelled after 3 upload failures';
    }
    if (uploadStatus.image.status === 'completed') {
      jobTrackingUpdates[FIELD_IMAGE_SOURCE_UPLOAD_STATUS] = 'Deleted - Force Cancelled after 3 upload failures';
      jobTrackingUpdates[`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_IMAGE_STATUS}`] = 'Deleted - Force Cancelled after 3 upload failures';
    }
    if (uploadStatus.video.status === 'completed') {
      jobTrackingUpdates[FIELD_VIDEO_SOURCE_UPLOAD_STATUS] = 'Deleted - Force Cancelled after 3 upload failures';
      jobTrackingUpdates[`${FIELD_JOB_JOB_STATUS}.${FIELD_JOB_STATUS_MAP_VIDEO_STATUS}`] = 'Deleted - Force Cancelled after 3 upload failures';
    }

    // Update the job tracking order id and payment status with Force Cancelled after upload failure status
    jobTrackingUpdates[FIELD_ORDER_ID] = 'Force Cancelled after 3 upload failures';
    jobTrackingUpdates[FIELD_ORDER_PAYMENT_STATUS] = 'Force Cancelled after 3 upload failures';
    await updateDoc(docRef, jobTrackingUpdates);    
    
    // Hide all other messages and buttons
    document.getElementById('overlayMessage').style.display = 'none';
    document.getElementById('errorMessage').style.display = 'none';
    document.getElementById('progressText').style.display = 'none';
    document.getElementById('uploadErrorButtonContainer').style.display = 'none';

    // Show the forced cancellation message and the Okay button
    const cancelMessage = document.getElementById('cancelMessage');
    const forcedCancellationButtonContainer = document.getElementById('forcedCancellationButtonContainer');
    cancelMessage.style.display = 'block';
    forcedCancellationButtonContainer.style.display = 'flex';
    
  } catch (error) {
    console.error('Error trying to delete the uploads and the job tracking document on force cancel due to 3 upload failures:', error);    
  }    
}

// Function to take an order snapshot, find the Shopify variant ID and add it as a line item to the cart
async function addOrderLineItemToCart() {
  
  // Get the product variant of the item in orderSnapshot
  const productVariantId = await getProductVariant();

  if (!productVariantId) {
    console.error("Failed to get the product variant ID when adding the order line item to cart");
  }

  // Upload mockup thumbnail from orderSnaphot to Cloud Storage and get URL to be used with line item on cart
  try {

    // Assign variables for Cloud Storage upload
    const storage = getStorage();
    const mockupImageForCartCloudStoragePath = `custom-canvas-mockup-images-for-cart/${orderSnapshot.jobId}_mockupImageForCart.png`;

    // Get the document reference for the tracking document to update the tracking document
    const docRef = doc(getFirestore(), FIRESTORE_JOB_TRACKING_COLLECTION, orderSnapshot.jobId);    

    // Assign mockup thumnbail image metadata
    const customMetadata = {
      contentType: "image/png",
      customMetadata: {
        jobId: orderSnapshot.jobId,
      },
    };

    const mockupImageForCartUploadTaskSnapshot = await uploadBytes(
      ref(storage, mockupImageForCartCloudStoragePath),
      orderSnapshot.mockupThumbnail,
      customMetadata
    );

    // Update job tracking document with Cloud Storage path for mockup thumbnail
    await updateDoc (docRef, {
      [FIELD_MOCKUP_THUMBNAIL_FOR_CART_PATH]: mockupImageForCartCloudStoragePath
    });

    // Get download URL to be used in the line item properties for the cart and add it to the orderSnapshot
    const mockupThumbnailDownloadUrl = await getDownloadURL(ref(storage, mockupImageForCartCloudStoragePath));
    orderSnapshot.mockupThumbnailUrl = mockupThumbnailDownloadUrl;
    
  } catch (error) {
    console.log ("Error uploading mockup thumbnail to Cloud Storage: ", error );
  }


  // Prepare the custom properties from orderSnapshot we want passed to the cart
  const properties = {
    "Canvas Name": orderSnapshot.canvasExperienceName,
    "Please Note": "The thumbnail will display your custom image in the cart, but a generic thumnbail image will appear at checkout",
    "_jobId": orderSnapshot.jobId,
    "_mockupThumbnailUrl": orderSnapshot.mockupThumbnailUrl
  };

  // Send a POST request to Shopify's cart/add.js
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          quantity: 1,
          id: productVariantId,
          properties: properties
        }]
      }),
    });
    

    if (response.ok) {
      const responseData = await response.json();

      // If you want to log the response for debugging purposes...
      //console.log('Item added to cart:', responseData);
      
      // Redirect to the cart or update the cart UI
      window.location.href = '/cart';  // Redirect to the cart page
      
    } else {
      console.error('Error adding item to cart:', response.status, response.statusText);

          // Show the error message for the add to cart error
    // Replace overlay message with error message
    const overlayMessage = document.getElementById('overlayMessage');
    const addToCartErrorMessage = document.getElementById('addToCartErrorMessage');
    const progressText = document.getElementById('progressText');
    const forcedCancellationButtonContainer = document.getElementById('forcedCancellationButtonContainer');    

    // Hide current overlay message and progress text
    overlayMessage.style.display = 'none';
    progressText.style.display = 'none';

    // Show error message and error buttons
    addToCartErrorMessage.style.display = 'block';
    forcedCancellationButtonContainer.style.display = 'flex';
    }  
    
    
  } catch (error) {
    console.error('Error adding item to cart:', error);

    // Show the error message for the add to cart error
    // Replace overlay message with error message
    const overlayMessage = document.getElementById('overlayMessage');
    const addToCartErrorMessage = document.getElementById('addToCartErrorMessage');
    const progressText = document.getElementById('progressText');
    const forcedCancellationButtonContainer = document.getElementById('forcedCancellationButtonContainer');    

    // Hide current overlay message and progress text
    overlayMessage.style.display = 'none';
    progressText.style.display = 'none';

    // Show error message and error buttons
    addToCartErrorMessage.style.display = 'block';
    forcedCancellationButtonContainer.style.display = 'flex';
  }  
}

// Returns the product variant for the current product in the orderSnapshot object
async function getProductVariant () {

  // Get the product handle based on the canvas shape
  let productHandle;
  switch (orderSnapshot.aspectRatio) {
    case AspectRatio.SQUARE:
      productHandle = 'custom-ar-canvas-square';
      break;
    case AspectRatio.PORTRAIT:
      productHandle = 'custom-ar-canvas-portrait';
      break;
    case AspectRatio.LANDSCAPE:
      productHandle = 'custom-ar-canvas-landscape';
      break;
    default:
      console.error("Invalid aspect ratio when trying to return product variant based on orderSnapshot");
      return;
  }

  // Size mappings for the Shopify listed size variants
  const sizeMappings = {
    square: {
      [CanvasSize.SMALL]: '12" x 12"',
      [CanvasSize.MEDIUM]: '16" x 16"',
      [CanvasSize.LARGE]: '24" x 24"',
      [CanvasSize.X_LARGE]: '36" x 36"',
    },
    portrait: {
      [CanvasSize.SMALL]: '12" x 18"',
      [CanvasSize.MEDIUM]: '16" x 24"',
      [CanvasSize.LARGE]: '24" x 36"',
      [CanvasSize.X_LARGE]: '32" x 48"',
    },
    landscape: {
      [CanvasSize.SMALL]: '18" x 12"',
      [CanvasSize.MEDIUM]: '24" x 16"',
      [CanvasSize.LARGE]: '36" x 24"',
      [CanvasSize.X_LARGE]: '48" x 32"',
    }
  }

  // Use the size mappings to get the Shopify size variant based on the canvas size
  const sizeVariant = sizeMappings[orderSnapshot.aspectRatio][orderSnapshot.canvasSize];
  if (!sizeVariant) {
    console.error("Invalid canvas size when trying to return product variant based on orderSnapshot");
    return;
  }

  // Get the Shopify Frame Depth variant based on the frame depth
  const frameDepthVariant = orderSnapshot.frameDepth === FrameDepth.INCH75 ? '.75"' : '1.5"';

  try {
    // Query Shopify AJAX API to get the all the variants for this product handle
    const response  = await fetch(`/products/${productHandle}.json`);
    const product = await response.json();

    // Get the correct variant by filtering based on size and frame depth
    const variants = product.product.variants;
    const correctVariant = variants.find(variant => {
      const size = variant.option1;      
      const frameDepth = variant.option2;

      // Adjustment made since frame depth was removed as a variant option in a way where it can still be used in case 
      // it comes back at a later time -- We test if the frameDepth variable assigned with variant.option2 is null or 
      // undefined, and we use that to determine what we look for to return
      if (frameDepth !== undefined && frameDepth !== null) {
        return size === sizeVariant && frameDepth === frameDepthVariant;
        
      } else {
        return size === sizeVariant;        
      }
    });

    if (correctVariant) {
      return correctVariant.id;
    } else {
      console.error('No matching variant found when trying to get product variant based on orderSnapshot');
      return null;
    }
  } catch (error) {
    console.error('Error fetching product variants in getProductVariant:', error);
    return null;    
  }
}

// Function to get the canvas width and height in inches based on sleected canvas size and aspect ratio
function getCanvasDimensions(aspectRatio, canvasSize) {
  // Define the dimensions in inches for each size and shape canvas
  const aspectRatioDimensions = {
    [AspectRatio.SQUARE]: {
      [CanvasSize.SMALL]: { width: 12, height: 12 },
      [CanvasSize.MEDIUM]: { width: 16, height: 16 },
      [CanvasSize.LARGE]: { width: 24, height: 24 },
      [CanvasSize.X_LARGE]: { width: 36, height: 36 },
    },
    [AspectRatio.PORTRAIT]: {
      [CanvasSize.SMALL]: { width: 12, height: 18 },
      [CanvasSize.MEDIUM]: { width: 16, height: 24 },
      [CanvasSize.LARGE]: { width: 24, height: 36 },
      [CanvasSize.X_LARGE]: { width: 32, height: 48 },
    },
    [AspectRatio.LANDSCAPE]: {
      [CanvasSize.SMALL]: { width: 18, height: 12 },
      [CanvasSize.MEDIUM]: { width: 24, height: 16 },
      [CanvasSize.LARGE]: { width: 36, height: 24 },
      [CanvasSize.X_LARGE]: { width: 48, height: 32 },
    },
  };

  // Lookup dimensions from map above using selected aspect ratio and canvas size
  const dimensions = aspectRatioDimensions[aspectRatio][canvasSize];

  if (!dimensions) {
    console.error("Invalid aspect ratio or canvas size");
    return { canvasWidth: 0, canvasHeight: 0 };
  }

  // Get canvas width and height in inches and return
  const canvasWidth = dimensions.width;
  const canvasHeight = dimensions.height;

  return { canvasWidth, canvasHeight };
}

// Function to generate the permanent 10-letter identifier for the AR package
function generateArPackageCode () {
  //
  // AR PACKAGE CODES
  // AR package codes should persist among AR Packages, or AR Experiences. These are the package combination of a target
  // image and its associated video. The app will always associate a package to one specific target image. The AR packages
  // and target images work, irrespective of size. So the AR package code will be the same among different sized canvases.
  // The AR package will be the base of the product for a specific canvas where the product code will consist of
  // [AR package code]-[Canvas Type]-[Canvas Size]. For example CLRBLYDNCR-CANPO-24X36 product code means an AR package
  // code of CLRBLYDNCR on Portrait Canvas that 24 x 36 inches in size -- For QR codes, we do not need the product code,
  // we will only need the AR package code appended to the deep link. An example is https://a.arteezart.com/p?CLRBLYDNCR
  // where https://a.arteezart.com/p? is the prepend for the deep link and CLRBLYDNCR is the package code -- Note: The 
  // domain in the prepended deep link is now the verification check for an AR Teez QR code instead of the previous
  // ARTEEZ prepend method we used prior. 
  //
  // The goal for creating AR package codes is to generate a unique 10-letter code each time, where the codes are
  // not sequential and do not follow a common visual pattern so that codes generated around a similar time do not look
  // similar. We do this so it significantly reduces the risk that someone mistyping their code slightly does not download
  // a different experience that was generated at the same time. In this scenario, it is much more likely that incorrect
  // codes that look similar to a correct code are non-working codes not associated with any AR Package. Here is what we do:
  // 1) The code must be 10 letters, unique each time, non-visually sequential, and never repeat
  // 2) A 10-letter code has 141,167,095,653,376 permutations
  // 3) The strategy involves generating the 10-letter code based on the current time, add'l random elements, and a dynamic transformation
  // 4) We start using the current elapsed seconds since Jan 1, 1970 at order time (this ensures the codes will never repeat)
  // 5) We then use a base-26 encoding on that number, which reduces the 10-digit number to a 7-digit letter code
  // 6) Orders processed in the same second would have the same code, so we add 3 random letters from the alphabet to create variance within that second
  // 7) We then use a dynamic affine transformation to the code to change the letters in a way that is deterministic and reversible
  // 8) The dynamic affine transformation ensures that codes created around the same time do not resemble each other

  // Set a random string generator for letters  
  const letterCharacters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  function generateRandomString(length) {
    let result = "";

    for (let i = 0; i < length; i++) {
      result += letterCharacters.charAt(
        Math.floor(Math.random() * letterCharacters.length)
      );
    }

    return result;
  }

  // First, get the elapsed seconds since Jan 1, 1970 at current time and convert to base-26 
  // letter encoding for the first 7 letters of the 10-letter AR package code
  let currentTimestamp = Math.floor(Date.now() / 1000);
  let untransformedArPackageCode = "";

  while (currentTimestamp > 0) {
    untransformedArPackageCode =
      letterCharacters[currentTimestamp % 26] +
      untransformedArPackageCode;

    currentTimestamp = Math.floor(currentTimestamp / 26);
  }

  // Second, add three random letters to distinguish codes generated in the same second
  untransformedArPackageCode += generateRandomString(3);

  // Third, apply dynamic affine transform codes generated near each other do not look sequential
  // and return this as the AR package code
  return applyDynamicAffineTransformToArPackageCode(untransformedArPackageCode);
}

// Function to generate product code for custom canvas
function generateProductCode(arPackageCode, aspectRatio, canvasSize, frameDepth) {
  //
  // PRODUCT CODES
  // Product codes should persist among the same product and have an appended code that describes the canvas variation
  // For example, CLRBLYDNCR-CANPO-15-24X36 (for Canvas Portrait 1.5in Frame at 24x36 -- For the QR code, we don't need
  // the -CANPO-15-24X36 as that just describes the product variation, and it's not necessary to differentiate between 
  // canvas variations for AR packages and scanning -- The QR code would then just be the CLRBLYDNCR prepended with the
  // deep link portion of https://a.arteezart.com/p? to create a complete QR code of https://a.arteezart.com/p?CLRBLYDNCR.
  // For the code that is visually displayed on the QR code image, we will remove the deep link part so it only displays
  // CLRBYLDNCR. We also account for the prepend part in the app -- The deep link domain is also what is now used as
  // the verification check for an AR Teez QR code instead of the previous ARTEEZ prepend method we used prior. 

  // Get canvas aspects and create code maps for the variation part of the product codes
  const aspectRatioMap = {
    [AspectRatio.SQUARE]: "CANSQ",
    [AspectRatio.PORTRAIT]: "CANPO",
    [AspectRatio.LANDSCAPE]: "CANLA",
  };

  const frameDepthMap = {
    [FrameDepth.INCH75]: "75",
    [FrameDepth.INCH150]: "15",
  };

  const { canvasWidth, canvasHeight } = getCanvasDimensions(
    aspectRatio,
    canvasSize
  );

  // Combine the AR package code with canvas variation, including canvas type and dimensions to get the product code
  const productCode = `${arPackageCode}-${aspectRatioMap[aspectRatio]}-${frameDepthMap[frameDepth]}-${canvasWidth}X${canvasHeight}`;

  return productCode;  
}

// Function to create a dynamic affine transformation to a letters-only base-26 code (for the AR package code)
// Used to make sequentially generated codes not look sequential -- they are still unique, deterministic, and reversible
function applyDynamicAffineTransformToArPackageCode(untransformedArPackageCode) {
  // This dynamic affine transformation is specifically for a base-26 code -- The aBase value is specifically
  // chosen because 5 is coprime to 26, bBase is just the chosen shift value, and m is equal the base used
  // Do not change these values after starting to use them in production as it will break the compatability and
  // reversibility of the codes user prior and after
  const aBase = 5;
  const bBase = 3;
  const m = 26;

  const totalMod = Array.from(untransformedArPackageCode).reduce((acc, c) => acc + c.charCodeAt(0), 0) % m;

  return Array.from(untransformedArPackageCode)
    .map((c, i) => {
      if (/[A-Z]/.test(c)) {
        // Confirm character is uppercase
        let cNum = c.charCodeAt(0) - "A".charCodeAt(0);
        let a = (aBase + i * totalMod) % m;
        let b = (bBase + totalMod) % m;
        let y = (a * cNum + b) % m;
        return String.fromCharCode(y + "A".charCodeAt(0));
      }
      return c;
    })
    .join("");
}

// Function to create a dynamic affine transformation to an alphanumeric base-36 code (for the middle part of the order IDs)
// Used to make sequentially generated codes not look sequential -- they are still unique, deterministic, and reversible
function applyDynamicAffineTransformToOrderId(midPortionOrderId) {
  // This dynamic affine transformation is specifically for a base-36 code -- The aBase value is specifically
  // chosen because 7 is coprime to 36, bBase is just the chosen shift value, and m is equal the base used
  // Do not change these values after starting to use them in production as it will break the compataibility and
  // reversibility of the codes user prior and after
  const aBase = 7;
  const bBase = 3;
  const m = 36;

  const totalMod =
    Array.from(midPortionOrderId).reduce((acc, c) => acc + parseInt(c, 36), 0) %
    m;

  return Array.from(midPortionOrderId)
    .map((c, i) => {
      let cNum = parseInt(c, 36);
      let a = (aBase + i * totalMod) % m;
      let b = (bBase + totalMod) % m;
      let y = (a * cNum + b) % m;
      return y.toString(36).toUpperCase();
    })
    .join("");
}

// Function to generate a QR Code based on the AR package resolver URL and using template images stored in Firebase
function generateQRCode(
  arPackageCode,
  qrCodeLogoUrl,
  qrCodeBackgroundUrl,
  qrCodeFrameUrl
) {

  // Create the AR package URL by adding the AR package code to the deep link package resolver URL
  const arPackageUrl = `https://a.arteezart.com/p?${arPackageCode}`;

  // Use the JSON below to create the QR code based on the QR Code Styling API
  // const QRCodeStyling = require("qr-code-styling");
  const qrCodeStylingObject = new QRCodeStyling({
    width: 584,
    height: 584,
    data: arPackageUrl,
    margin: 0,
    qrOptions: {
      typeNumber: 5,
      mode: "Byte",
      errorCorrectionLevel: "H",
    },
    imageOptions: {
      hideBackgroundDots: false,
      imageSize: 0.7,
      margin: 0,
      crossOrigin: "anonymous",
    },
    dotsOptions: {
      type: "extra-rounded",
      color: "#363375",
    },
    backgroundOptions: {
      color: "#69b6d1",
    },
    image: qrCodeLogoUrl,
    dotsOptionsHelper: {
      colorType: {
        single: true,
        gradient: false,
      },
      gradient: {
        linear: true,
        radial: false,
        color1: "#6a1a4c",
        color2: "#6a1a4c",
        rotation: "0",
      },
    },
    cornersSquareOptions: {
      type: "extra-rounded",
      color: "#363375",
    },
    cornersSquareOptionsHelper: {
      colorType: {
        single: true,
        gradient: false,
      },
      gradient: {
        linear: true,
        radial: false,
        color1: "#000000",
        color2: "#000000",
        rotation: "0",
      },
    },
    cornersDotOptions: {
      type: "dot",
      color: "#000000",
    },
    cornersDotOptionsHelper: {
      colorType: {
        single: true,
        gradient: false,
      },
      gradient: {
        linear: true,
        radial: false,
        color1: "#000000",
        color2: "#000000",
        rotation: "0",
      },
    },
    backgroundOptionsHelper: {
      colorType: {
        single: true,
        gradient: false,
      },
      gradient: {
        linear: true,
        radial: false,
        color1: "#ffffff",
        color2: "#ffffff",
        rotation: "0",
      },
    },
  });

  // Create a canvas to draw the QR code on -- Pixel dimensions all correspond to exact
  // pixel dimensions from saved images on Firebase as well as the exact pixel dimensions
  // for the printed canvas artwork at 300 pixels per inch (So QR code is 2in wide)
  const qrCodeCanvas = document.createElement("canvas");
  const ctx = qrCodeCanvas.getContext("2d");
  qrCodeCanvas.width = 600;
  qrCodeCanvas.height = 694;

  // Define function to load an image
  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        // console.log("Successfully loaded image from: ", url);
        resolve(img);
      };
      img.onerror = (errorEvent) => {
        // console.error("Error loading image from: ", url, errorEvent);
        reject(new Error(`Failed to load image from ${url}`));
      };
      img.src = url;
    });
  }

  // Note: I uploaded the following CORS config file to my Firebase Storage bucket to ensure
  // this domain could pull images from Firebase, layer them on a canvas, and then export
  // them -- So if I ever change the domain, I will need to update the CORS file
  // [
  //     {
  //         "origin": ["https://arteezart.com"],
  //         "responseHeader": ["Content-Type"],
  //         "method": ["GET"],
  //         "maxAgeSeconds": 3600
  //     }
  // ]

  // Wrap everything in a promise because this is asynchronous and we want to return a Blob at the end
  return new Promise((resolve, reject) => {
    // Drawing starts with converting the generated QR code styling object raw data into blob
    // on a temporary URL that allows to load it as an image
    qrCodeStylingObject
      .getRawData("png")
      .then((blob) => {
        const qrCodeBlobUrl = URL.createObjectURL(blob);

        // Load the QR Code image
        return loadImage(qrCodeBlobUrl).then((qrCodeImg) => {
          // Cleanup the temp URL and pass the loaded QR code image to the next step
          URL.revokeObjectURL(qrCodeBlobUrl);
          return qrCodeImg;
        });
      })
      .then((qrCodeImg) => {
        // First, draw QR code background
        return loadImage(qrCodeBackgroundUrl).then((backgroundImg) => {
          ctx.drawImage(
            backgroundImg,
            0,
            0,
            qrCodeCanvas.width,
            qrCodeCanvas.height
          );
          // Then, draw QR code image
          ctx.drawImage(qrCodeImg, 9, 102, 585, 585);
          // Clean up temporary blob URL
          URL.revokeObjectURL(qrCodeImg.src);
          // Load and draw QR code frame image
          return loadImage(qrCodeFrameUrl);
        });
      })
      .then((frameImg) => {
        // Draw the QR code frame image as the top layer
        ctx.drawImage(frameImg, 0, 0, qrCodeCanvas.width, qrCodeCanvas.height);

        // Now add the display code text, which is just the AR package code now
        const displayCode = arPackageCode;

        // Set up the font and font size -- Dyanmically resize font to ensure it fits within the max width area
        const displayCodeMaxWidth = 540;
        let fontSize = 60;
        let textWidth;

        do {
          ctx.font = `bold ${fontSize}px 'Segoe UI', 'Helvetica Neue', 'Roboto', sans-serif`;
          ctx.fillStyle = "black";
          textWidth = ctx.measureText(displayCode).width;
          // console.log("textWidth: " + textWidth);
          if (textWidth > displayCodeMaxWidth) fontSize -= 1;
        } while (textWidth > displayCodeMaxWidth && fontSize > 1);

        // Center the text in the text area and draw it to the canvas
        const textX = (qrCodeCanvas.width - textWidth) / 2;
        const textY = 54 + fontSize / 3;
        ctx.fillText(displayCode, textX, textY);

        // Convert canvas to a Blob and return it
        qrCodeCanvas.toBlob((blob) => {
          resolve(blob);
        }, "image/png");

        // Uncomment the following code block to have the QR code show on the webpage for debugging
        /*
      // Create the final composite image
      const finalImg = new Image();
      finalImg.onload = () => {
        document.getElementById("qrcode").appendChild(finalImg);
      };
      finalImg.src = qrCodeCanvas.toDataURL("image/png");
      */
      })
      .catch((error) => {
        console.error("Failed to load images or generate QR code: ", error);
        reject(error);
      });
  });
}

// Function to call my HTTP Cloud Function to send failure emails when the QR code, image, or video source
// asset does not upload successfully -- Uses Firebase Auth to get an ID token to for security, HTTP Cloud
// Function is setup to require a Firebase Auth to run
function callSendEmailHTTPFunctionOnUploadFailure(
  failedUploadAsset,
  orderSnapshot
) {
  // Convert failed upload asset to a formatted string for the email
  let problemAssetString;
  if (failedUploadAsset === "qrCode") {
    problemAssetString = "QR Code Source Image";
  } else if (failedUploadAsset === "image") {
    problemAssetString = "Source Image";
  } else if (failedUploadAsset === "video") {
    problemAssetString = "Source Video";
  } else {
    problemAssetString = "Undefined";
  }

  // Construct the email subject and body
  const emailData = {
    subject: `Custom Canvas Job Failure for ${orderSnapshot.jobId}: Source Asset Upload Error`,
    body: `Source Asset Upload Error: Couldn't upload asset from Shopify to Cloud Storage


    JOB ID: ${orderSnapshot.jobId}

    ASSET: ${problemAssetString}

    ORDER ID: ${orderSnapshot.orderId}


    -- Requires manual intervention --
    `,
  };

  // Get Firebase auth token then call function with token
  auth.currentUser
    .getIdToken(true)
    .then(function (idToken) {
      // Use fresh ID token for the secure call
      fetch(
        "https://us-central1-ar-teez.cloudfunctions.net/sendEmailWithZohoHTTP",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + idToken,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(emailData),
          // Add body or other headers as needed
        }
      )
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          console.log("Failure email sent successfully");
        })
        .catch((error) => {
          console.error("Error calling the HTTP send email function: ", error);
        });
    })
    .catch(function (error) {
      console.error("Error getting the ID token: ", error);
    });
}